import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '@/lib/api';
import { POOL_PLAN_PATHS, getShapeData, getPlacementZones, computeLayout, generatePoolPath } from '@/lib/poolShapeData';
import { getZonesForShape } from '@/lib/poolZones';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Check, ChevronLeft, ChevronRight, MapPin, Waves, Paintbrush, Wrench, Sparkles, Mountain, FileCheck, Calculator, Plus, Trash2, Info, Printer } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

const DEPTH_PROFILES = {
  flat: { label: 'Flat Bottom', previewLabel: 'Depth: Flat bottom', tooltip: 'Same depth across the whole pool (no deep end).' },
  sport_bottom: { label: 'Sport Bottom', previewLabel: 'Depth: Sport bottom (shallow both ends)', tooltip: 'Shallow at both ends with a slightly deeper center for games.' },
  deep_end_hopper: { label: 'Deep End Hopper', previewLabel: 'Depth: Deep-end hopper', tooltip: 'Shallow end sloping down into a deeper "hopper" at one end.' },
};

const ENTRY_TYPES = {
  corner_steps: { label: 'Corner Entry', description: 'Compact steps tucked into one corner of the shallow end', previewLabel: 'Steps: Corner entry' },
  side_steps: { label: 'Side Wall Entry', description: 'Steps along one side wall for a narrow profile', previewLabel: 'Steps: Side entry' },
  full_width_steps: { label: 'Full-Width Entry Steps', description: 'Wide steps spanning the entire shallow end', previewLabel: 'Steps: Full-width' },
  recessed_steps: { label: 'Recessed Swim-Out', description: 'Steps recessed into the wall, flush with pool interior', previewLabel: 'Steps: Recessed' },
  no_steps: { label: 'No Steps', description: 'Pool without built-in steps', previewLabel: '' },
};

const TANNING_LEDGES = {
  none: { label: 'None', description: '', previewLabel: '' },
  small: { label: 'Compact Sun Shelf', description: 'Compact shelf for one lounge chair (4–5 ft)', previewLabel: 'Shelf: Compact' },
  medium: { label: 'Standard Sun Shelf', description: 'Standard shelf for two loungers side by side (6–8 ft)', previewLabel: 'Shelf: Standard' },
  large: { label: 'Extended Sun Shelf', description: 'Broad shallow shelf spanning most of the shallow end (10+ ft)', previewLabel: 'Shelf: Extended' },
};

const BENCH_SEATING = {
  none: { label: 'None', description: '', previewLabel: '' },
  one_side: { label: 'Single-Wall Bench', description: 'Bench along one long wall of the pool', previewLabel: 'Bench: One wall' },
  deep_end: { label: 'Deep-End Bench', description: 'Submerged bench across the deep end for lounging', previewLabel: 'Bench: Deep end' },
  wraparound: { label: 'Wraparound Bench', description: 'Bench wrapping multiple walls for max seating', previewLabel: 'Bench: Wraparound' },
};

const SLIDE_PLACEMENTS = {
  none: { label: 'None', description: '', previewLabel: '' },
  left: { label: 'Left Side', description: 'Slide enters pool from the left', previewLabel: 'Slide: Left' },
  right: { label: 'Right Side', description: 'Slide enters pool from the right', previewLabel: 'Slide: Right' },
  deep_end: { label: 'Deep End', description: 'Slide at the deep end for maximum drop', previewLabel: 'Slide: Deep end' },
  custom: { label: 'Custom Placement', description: 'Non-standard slide position', previewLabel: 'Slide: Custom' },
};

const WATER_FEATURES = {
  none: { label: 'None', description: '', previewLabel: '' },
  sheer_descents: { label: 'Sheer Descents', description: 'Sheets of water flowing from raised wall', previewLabel: 'Water: Sheer descents' },
  bubblers: { label: 'Bubblers', description: 'Bubbling jets on sun shelf or shallow end', previewLabel: 'Water: Bubblers' },
  deck_jets: { label: 'Deck Jets', description: 'Arcing water streams from deck into pool', previewLabel: 'Water: Deck jets' },
  scuppers: { label: 'Scuppers', description: 'Wall-mounted spillway features', previewLabel: 'Water: Scuppers' },
  rock_waterfall: { label: 'Rock Waterfall', description: 'Natural stone waterfall cascading into pool', previewLabel: 'Water: Waterfall' },
  multiple: { label: 'Multiple Features', description: 'Combination of water features', previewLabel: 'Water: Multiple' },
};

const FIRE_FEATURES = {
  none: { label: 'None', description: '', previewLabel: '' },
  bowls: { label: 'Fire Bowls', description: 'Elevated fire bowls flanking the pool area', previewLabel: 'Fire: Bowls' },
  fire_pit: { label: 'Fire Pit', description: 'Dedicated fire pit near the pool', previewLabel: 'Fire: Pit' },
  both: { label: 'Bowls + Fire Pit', description: 'Fire bowls and a dedicated fire pit', previewLabel: 'Fire: Bowls + pit' },
};

const STEPS = [
  { label: 'Lead & Site', icon: MapPin },
  { label: 'Pool Basics', icon: Waves },
  { label: 'Finish & Deck', icon: Paintbrush },
  { label: 'Equipment', icon: Wrench },
  { label: 'Features', icon: Sparkles },
  { label: 'Site Difficulty', icon: Mountain },
  { label: 'Permits', icon: FileCheck },
  { label: 'Review', icon: Calculator },
];

export default function BidWizard() {
  const { id: editBidId } = useParams();
  const isEditMode = !!editBidId;
  const [step, setStep] = useState(0);
  const [inputs, setInputs] = useState({
    customer_name: '', address: '', city: '', source: '', timeline: '8-12 weeks',
    pool_type: 'gunite', length: 32, width: 16, avg_depth: 5, gallons: 0,
    finish_type: '', tile_type: '', coping_type: '', deck_sqft: 0, deck_material: '',
    deck_entries: [],
    equipment_package: 'mid', pump_type: '', filter_type: '', heater_type: '',
    salt_system: false, automation_level: '', lights_count: 3, drains: 1, skimmers: 1,
    waterfall: false, slide: false, tanning_ledge: false, spa: false, fire_features: false, retaining_wall: false,
    cover_type: 'none', cover_size: 'standard',
    access_difficulty: 'standard', soil_rock_risk: 'low', haul_distance: 'normal', crane_needed: false, rock_hard_dig: false,
    city_permit: '', structural_engineering: false, bond_required: false,
    custom_features: [],
    electric_distance: 30, gas_distance: 30,
    electric_included_ft: 30, gas_included_ft: 30,
    panel_upgrade: false, gas_meter_upgrade: false,
  });
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState('');
  const [creating, setCreating] = useState(false);
  const [permitCities, setPermitCities] = useState([]);
  const [addCityOpen, setAddCityOpen] = useState(false);
  const [newCity, setNewCity] = useState({ city: '', cost: 1000 });
  const [customFeatureOpen, setCustomFeatureOpen] = useState(false);
  const [customFeature, setCustomFeature] = useState({ name: '', description: '', cost: 0, sell_price: 0 });
  const navigate = useNavigate();

  useEffect(() => { api.get('/leads').then(r => setLeads(r.data)).catch(() => {}); }, []);

  // Load existing bid data in edit mode
  useEffect(() => {
    if (editBidId) {
      api.get(`/bids/${editBidId}`).then(r => {
        const bid = r.data;
        if (bid.inputs) {
          setInputs(prev => ({ ...prev, ...bid.inputs }));
        }
        if (bid.lead_id) setSelectedLead(bid.lead_id);
      }).catch(() => { toast.error('Failed to load bid'); navigate('/bids'); });
    }
  }, [editBidId]);

  const loadPermitCities = () => api.get('/pricing/permit-cities').then(r => setPermitCities(r.data)).catch(() => {});
  useEffect(() => { loadPermitCities(); }, []);

  const handleAddCity = async () => {
    if (!newCity.city.trim()) return;
    try {
      await api.post('/pricing/add-city-permit', { city: newCity.city.trim(), cost: parseFloat(newCity.cost) || 1000 });
      toast.success(`${newCity.city} permit added to pricing library`);
      setAddCityOpen(false);
      setNewCity({ city: '', cost: 1000 });
      loadPermitCities();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add city');
    }
  };

  const u = (field, value) => setInputs(p => ({ ...p, [field]: value }));

  const handleLeadSelect = (leadId) => {
    setSelectedLead(leadId);
    const lead = leads.find(l => l.id === leadId);
    if (lead) {
      setInputs(p => ({
        ...p,
        customer_name: lead.name || '',
        address: lead.address || '',
        city: lead.city || '',
        source: lead.source || '',
      }));
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      // Edit mode: update existing bid inputs, sync line items, and recompute
      if (isEditMode) {
        await api.put(`/bids/${editBidId}`, { inputs });
        await api.post(`/bids/${editBidId}/sync-items`);
        await api.post(`/bids/${editBidId}/compute-totals`);
        toast.success('Proposal updated!');
        navigate(`/bids/${editBidId}`);
        setCreating(false);
        return;
      }

      const r = await api.post('/bids', { lead_id: selectedLead || null, inputs });
      toast.success('Bid created! Adding line items...');
      const bidId = r.data.id;

      // Fetch utility pricing from library for accurate costs
      let utilPricing = {};
      try {
        const up = await api.get('/pricing', { params: { category: 'utilities' } });
        for (const item of up.data) { utilPricing[item.name] = item; }
      } catch {}

      // Auto-populate line items based on inputs
      const items = buildLineItems(utilPricing);
      for (const item of items) {
        await api.post(`/bids/${bidId}/line-items`, item);
      }

      // Apply equipment package if selected
      if (inputs.equipment_package) {
        await api.post(`/bids/${bidId}/apply-package?package=${inputs.equipment_package}`);
      }

      // Apply city permit
      if (inputs.city_permit) {
        try { await api.post(`/bids/${bidId}/apply-city-permit?city=${inputs.city_permit}`); } catch {}
      }

      // Apply rock dig contingency
      if (inputs.rock_hard_dig) {
        await api.post(`/bids/${bidId}/apply-rock-dig?pct=10`);
      }

      // Compute totals
      await api.post(`/bids/${bidId}/compute-totals`);
      toast.success('Bid ready!');
      navigate(`/bids/${bidId}`);
    } catch (err) {
      toast.error('Failed to create bid');
    }
    setCreating(false);
  };

  const buildLineItems = (utilPricing = {}) => {
    const items = [];
    const L = inputs.length || 32, W = inputs.width || 16, D = inputs.avg_depth || 5;
    const deckSF = inputs.deck_sqft || 0;

    // === HDH LOT PRICING (matches pricing library) ===

    // Excavation
    items.push({ category: 'excavation', name: 'Standard Dig', qty: 1, unit: 'LOT', unit_cost: 9375, markup_pct: 33, sell_price: 12500, notes: `Pool ${L}x${W}x${D}` });
    items.push({ category: 'excavation', name: 'Dirt Haul Off', qty: 1, unit: 'LOT', unit_cost: 2250, markup_pct: 33, sell_price: 3000 });
    items.push({ category: 'excavation', name: 'Grading & Compaction', qty: 1, unit: 'LOT', unit_cost: 1200, markup_pct: 33, sell_price: 1600 });

    // Steel
    items.push({ category: 'steel', name: 'Rebar Install', qty: 1, unit: 'LOT', unit_cost: 4500, markup_pct: 33, sell_price: 6000 });

    // Plumbing
    items.push({ category: 'plumbing', name: 'Rough Plumbing Package', qty: 1, unit: 'LOT', unit_cost: 6375, markup_pct: 33, sell_price: 8500 });
    if (inputs.spa) items.push({ category: 'plumbing', name: 'Spa Plumbing Package', qty: 1, unit: 'LOT', unit_cost: 2250, markup_pct: 33, sell_price: 3000 });

    // Electrical
    items.push({ category: 'electrical', name: 'Equipment Wiring & Bonding', qty: 1, unit: 'LOT', unit_cost: 3375, markup_pct: 33, sell_price: 4500 });

    // Gunite
    items.push({ category: 'gunite', name: 'Gunite Shell', qty: 1, unit: 'LOT', unit_cost: 16500, markup_pct: 33, sell_price: 22000 });

    // Tile & Coping (LOT allowances)
    items.push({ category: 'tile', name: 'Waterline Tile Band', qty: 1, unit: 'LOT', unit_cost: 3750, markup_pct: 33, sell_price: 5000, is_allowance: true, allowance_amount: 5000, notes: 'Tile allowance $15/SF' });
    items.push({ category: 'tile', name: 'Standard Coping', qty: 1, unit: 'LOT', unit_cost: 4875, markup_pct: 33, sell_price: 6500, notes: 'Coping allowance $40/LF' });

    // Plaster (LOT pricing)
    const isPebble = inputs.finish_type === 'Pebble Tec' || inputs.finish_type === 'QuartzScapes';
    items.push({ category: 'plaster', name: 'Standard Plaster', qty: 1, unit: 'LOT', unit_cost: 9000, markup_pct: 33, sell_price: 12000 });
    if (isPebble) {
      items.push({ category: 'plaster', name: 'Pebble Tec / Quartz Upgrade', qty: 1, unit: 'LOT', unit_cost: 4500, markup_pct: 33, sell_price: 6000, is_allowance: true, allowance_amount: 6000, notes: 'Upgrade to pebble/quartz $10/SF' });
    }

    // Decking — supports multiple entries
    const deckEntries = inputs.deck_entries || [];
    const deckSellMap = { 'Concrete Broom Finish': 12, 'Kool Deck': 10, 'Stamped Concrete': 16, 'Pavers - Standard': 20, 'Travertine Pavers': 27 };
    const deckCostMap2 = { 'Concrete Broom Finish': 9, 'Kool Deck': 7, 'Stamped Concrete': 12, 'Pavers - Standard': 15, 'Travertine Pavers': 20 };
    for (const de of deckEntries) {
      if (de.sqft > 0) {
        const sell = deckSellMap[de.material] || 12;
        const cost = deckCostMap2[de.material] || 9;
        items.push({ category: 'decking', name: de.material || 'Concrete Broom Finish', qty: de.sqft, unit: 'SF', unit_cost: cost, markup_pct: 33, sell_price: sell, notes: `${de.sqft}sf @ $${sell}/sf` });
      }
    }
    // Legacy single deck entry (backwards compat)
    if (deckEntries.length === 0 && deckSF > 0) {
      const deckMap = { 'Concrete Broom Finish': 12, 'Stamped Concrete': 16, 'Pavers': 20, 'Travertine Pavers': 27 };
      const deckCostMap = { 'Concrete Broom Finish': 9, 'Stamped Concrete': 12, 'Pavers': 15, 'Travertine Pavers': 20 };
      const sell = deckMap[inputs.deck_material] || 12;
      const cost = deckCostMap[inputs.deck_material] || 9;
      items.push({ category: 'decking', name: inputs.deck_material || 'Concrete Broom Finish', qty: deckSF, unit: 'SF', unit_cost: cost, markup_pct: 33, sell_price: sell, notes: `${deckSF}sf @ $${sell}/sf` });
    }

    // Features
    if (inputs.waterfall) items.push({ category: 'features', name: 'Natural Rock Waterfall', qty: 1, unit: 'LOT', unit_cost: 7500, markup_pct: 40, sell_price: 10500 });
    if (inputs.slide) items.push({ category: 'features', name: 'Pool Slide', qty: 1, unit: 'EA', unit_cost: 5250, markup_pct: 33, sell_price: 7500 });
    if (inputs.tanning_ledge) items.push({ category: 'features', name: 'Tanning Ledge', qty: 1, unit: 'EA', unit_cost: 1875, markup_pct: 33, sell_price: 2500 });
    if (inputs.spa) items.push({ category: 'features', name: 'Spa Spillover', qty: 1, unit: 'LOT', unit_cost: 3750, markup_pct: 33, sell_price: 5000 });
    if (inputs.fire_features) items.push({ category: 'features', name: 'Fire Bowl', qty: 2, unit: 'EA', unit_cost: 1125, markup_pct: 33, sell_price: 1500 });

    // Permits
    if (inputs.structural_engineering) items.push({ category: 'permits', name: 'Structural Engineering', qty: 1, unit: 'EA', unit_cost: 1125, markup_pct: 33, sell_price: 1500 });
    if (inputs.bond_required) items.push({ category: 'misc', name: 'Contractor Bond', qty: 1, unit: 'LOT', unit_cost: 500, markup_pct: 30, sell_price: 650 });

    // Utilities — simple included + extra, no shared trench, $18/$24 rates
    const elecIncluded = inputs.electric_included_ft || 30;
    const gasIncluded = inputs.gas_included_ft || 30;
    const elecDist = inputs.electric_distance || 0;
    const gasDist = inputs.gas_distance || 0;

    // Electric included
    items.push({ category: 'utilities', name: 'Electric Run - Included (up to 30 LF)', qty: 1, unit: 'LOT',
      unit_cost: 0, markup_pct: 0, sell_price: 0,
      notes: `Electric run to house — up to ${elecIncluded}ft included.` });

    const extraElec = Math.max(0, elecDist - elecIncluded);
    if (extraElec > 0) {
      items.push({ category: 'utilities', name: 'Electric Run - Additional per LF', qty: extraElec, unit: 'LF',
        unit_cost: 12, markup_pct: 50, sell_price: 18,
        notes: `${extraElec}ft beyond ${elecIncluded}ft included @ $18/ft` });
    }

    // Gas included
    items.push({ category: 'utilities', name: 'Gas Run - Included (up to 30 LF)', qty: 1, unit: 'LOT',
      unit_cost: 0, markup_pct: 0, sell_price: 0,
      notes: `Gas run to house — up to ${gasIncluded}ft included.` });

    const extraGas = Math.max(0, gasDist - gasIncluded);
    if (extraGas > 0) {
      items.push({ category: 'utilities', name: 'Gas Run - Additional per LF', qty: extraGas, unit: 'LF',
        unit_cost: 16, markup_pct: 50, sell_price: 24,
        notes: `${extraGas}ft beyond ${gasIncluded}ft included @ $24/ft` });
    }

    // Upgrades
    if (inputs.panel_upgrade) {
      items.push({ category: 'utilities', name: 'Utility Upgrade - Electrical Panel', qty: 1, unit: 'LOT',
        unit_cost: 2625, markup_pct: 33, sell_price: 3500 });
    }
    if (inputs.gas_meter_upgrade) {
      items.push({ category: 'utilities', name: 'Utility Upgrade - Gas Meter', qty: 1, unit: 'LOT',
        unit_cost: 1875, markup_pct: 33, sell_price: 2500 });
    }

    // Misc
    items.push({ category: 'misc', name: 'Pool Fill Water', qty: 1, unit: 'LOT', unit_cost: 150, markup_pct: 33, sell_price: 200 });
    items.push({ category: 'misc', name: 'Startup Service & Chemical Kit', qty: 1, unit: 'LOT', unit_cost: 375, markup_pct: 33, sell_price: 500 });
    items.push({ category: 'misc', name: 'Project Supervision', qty: 1, unit: 'LOT', unit_cost: 2250, markup_pct: 33, sell_price: 3000, notes: 'HDH project management' });

    // Pool Cover
    const coverMap = {
      winter: { name: 'Winter / Tarp Cover (manual)', cost: 450, sell: 750 },
      solar: { name: 'Solar Cover + Reel', cost: 900, sell: 1500 },
      safety_mesh: { name: 'Manual Safety Cover (mesh)', cost: 2280, sell: 3800 },
      safety_solid: { name: 'Manual Safety Cover (solid)', cost: 2500, sell: 4200 },
      auto: { name: 'Automatic Safety Cover - Standard', cost: 12000, sell: 18000 },
      auto_complex: { name: 'Automatic Safety Cover - Complex/Large', cost: 15000, sell: 22000 },
      sliding_deck: { name: 'Sliding Deck / Hard-Top System', cost: 32500, sell: 50000 },
    };
    const sizeFactor = { small: 0.85, standard: 1.0, large: 1.15, complex: 1.30 };
    if (inputs.cover_type && inputs.cover_type !== 'none') {
      const cover = coverMap[inputs.cover_type];
      if (cover) {
        const factor = sizeFactor[inputs.cover_size] || 1.0;
        const adjSell = Math.round(cover.sell * factor);
        const adjCost = Math.round(cover.cost * factor);
        const notes = inputs.cover_type === 'auto' ? 'Includes system, track, and standard installation. Complex designs or lids may require additional pricing.' : '';
        items.push({ category: 'covers', name: cover.name, qty: 1, unit: 'EA', unit_cost: adjCost, markup_pct: 0, sell_price: adjSell, notes: notes + (factor !== 1 ? ` (${inputs.cover_size} factor: ${factor}x)` : '') });
      }
    }

    // Custom features / items
    for (const cf of (inputs.custom_features || [])) {
      items.push({
        category: cf.category || 'custom', name: cf.name, qty: 1, unit: 'LOT',
        unit_cost: parseFloat(cf.cost) || 0, markup_pct: 0,
        sell_price: parseFloat(cf.sell_price) || 0,
        notes: cf.description || '',
      });
    }

    return items;
  };

  const renderStep = () => {
    switch (step) {
      case 0: return (
        <div className="space-y-4 animate-fade-in">
          <h2 className="text-lg font-bold text-slate-900">Lead & Site Information</h2>
          {leads.length > 0 && (
            <div>
              <Label>Link to Existing Lead</Label>
              <Select value={selectedLead} onValueChange={handleLeadSelect}>
                <SelectTrigger data-testid="wizard-lead-select"><SelectValue placeholder="Select a lead (optional)" /></SelectTrigger>
                <SelectContent>{leads.map(l => <SelectItem key={l.id} value={l.id}>{l.name} - {l.city}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div><Label>Customer Name *</Label><Input value={inputs.customer_name} onChange={e => u('customer_name', e.target.value)} data-testid="wizard-customer-name" /></div>
          <div><Label>Address</Label><Input value={inputs.address} onChange={e => u('address', e.target.value)} data-testid="wizard-address" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>City</Label><Input value={inputs.city} onChange={e => u('city', e.target.value)} data-testid="wizard-city" /></div>
            <div><Label>Source</Label>
              <Select value={inputs.source} onValueChange={v => u('source', v)}>
                <SelectTrigger data-testid="wizard-source"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>{['Referral','Website','Home Show','Social Media','Other'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Timeline</Label><Input value={inputs.timeline} onChange={e => u('timeline', e.target.value)} data-testid="wizard-timeline" /></div>
        </div>
      );
      case 1: return (
        <div className="space-y-5 animate-fade-in">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Pool Basics</h2>

          {/* Group 1: Pool Type + Shape */}
          <div className="space-y-3">
            <p className="text-[10px] font-bold text-[#F59E0B] uppercase tracking-wider">Pool Type & Shape</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Construction Type *</Label>
                <Select value={inputs.pool_type} onValueChange={v => u('pool_type', v)}>
                  <SelectTrigger data-testid="wizard-pool-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gunite">Gunite/Shotcrete</SelectItem>
                    <SelectItem value="fiberglass">Fiberglass</SelectItem>
                    <SelectItem value="vinyl">Vinyl Liner</SelectItem>
                  </SelectContent>
                </Select></div>
            </div>
            <fieldset>
              <legend className="text-xs font-medium text-slate-700 mb-2">Shape / Layout *</legend>
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" role="radiogroup" aria-label="Pool shape" data-testid="pool-shape-picker">
                {[
                  { value: 'rectangle', label: 'Rectangle', icon: <svg viewBox="0 0 48 32" className="w-full h-full"><rect x="6" y="4" width="36" height="24" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none"/></svg> },
                  { value: 'auto_cover_rectangle', label: 'Auto-Cover', icon: <svg viewBox="0 0 48 32" className="w-full h-full"><rect x="6" y="4" width="36" height="24" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none"/><line x1="6" y1="10" x2="42" y2="10" stroke="currentColor" strokeWidth="0.8" strokeDasharray="2 2"/></svg> },
                  { value: 'roman', label: 'Roman', icon: <svg viewBox="0 0 48 32" className="w-full h-full"><path d="M14 4 L34 4 A8 12 0 0 1 34 28 L14 28 A8 12 0 0 1 14 4 Z" stroke="currentColor" strokeWidth="1.5" fill="none"/></svg> },
                  { value: 'freeform', label: 'Freeform', icon: <svg viewBox="0 0 48 32" className="w-full h-full"><path d="M12 8 C18 3, 30 5, 38 8 C43 12, 40 20, 36 24 C30 29, 18 28, 12 24 C7 20, 7 12, 12 8 Z" stroke="currentColor" strokeWidth="1.5" fill="none"/></svg> },
                  { value: 'grecian', label: 'Grecian', icon: <svg viewBox="0 0 48 32" className="w-full h-full"><path d="M12 4 L36 4 L42 10 L42 22 L36 28 L12 28 L6 22 L6 10 Z" stroke="currentColor" strokeWidth="1.5" fill="none"/></svg> },
                  { value: 'l_shape', label: 'L-Shape', icon: <svg viewBox="0 0 48 32" className="w-full h-full"><path d="M8 4 L28 4 L28 16 L42 16 L42 28 L8 28 Z" stroke="currentColor" strokeWidth="1.5" fill="none"/></svg> },
                  { value: 'lazy_l', label: 'Lazy-L', icon: <svg viewBox="0 0 48 32" className="w-full h-full"><path d="M8 4 L24 4 L24 12 L40 12 L40 28 L8 28 Z" stroke="currentColor" strokeWidth="1.5" fill="none"/></svg> },
                  { value: 'kidney', label: 'Kidney', icon: <svg viewBox="0 0 48 32" className="w-full h-full"><path d="M14 6 C24 2, 38 6, 40 16 C42 26, 30 30, 24 26 C20 24, 20 20, 22 16 C24 12, 14 10, 14 6 Z" stroke="currentColor" strokeWidth="1.5" fill="none"/></svg> },
                  { value: 'geometric_custom', label: 'Custom', icon: <svg viewBox="0 0 48 32" className="w-full h-full"><path d="M10 8 L22 4 L38 8 L42 20 L32 28 L14 26 L6 18 Z" stroke="currentColor" strokeWidth="1.5" fill="none"/></svg> },
                  { value: 'compact_plunge', label: 'Plunge', icon: <svg viewBox="0 0 48 32" className="w-full h-full"><rect x="12" y="6" width="24" height="20" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/></svg> },
                  { value: 'negative_edge', label: 'Neg. Edge', icon: <svg viewBox="0 0 48 32" className="w-full h-full"><rect x="6" y="4" width="36" height="24" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none"/><line x1="6" y1="28" x2="42" y2="28" stroke="currentColor" strokeWidth="3"/></svg> },
                ].map(shape => {
                  const selected = inputs.pool_shape_template === shape.value;
                  return (
                    <label key={shape.value}
                      className={`flex flex-col items-center p-2 rounded-lg border-2 cursor-pointer transition-all has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[#F59E0B] has-[:focus-visible]:ring-offset-1 ${selected ? 'border-[#F59E0B] bg-amber-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                      data-testid={`shape-${shape.value}`}>
                      <input type="radio" name="pool_shape_template" value={shape.value} checked={selected}
                        onChange={() => {
                          u('pool_shape_template', shape.value);
                          const defaults = {
                            rectangle: { depth_profile_type: 'sport_bottom', entry_type: 'corner_steps' },
                            auto_cover_rectangle: { depth_profile_type: 'sport_bottom', entry_type: 'corner_steps' },
                            freeform: { depth_profile_type: 'sport_bottom', entry_type: 'full_width_steps' },
                            compact_plunge: { shallow_depth: 4, deep_depth: 5, depth_profile_type: 'flat' },
                            negative_edge: { view_condition: 'view_lot' },
                          };
                          if (defaults[shape.value]) {
                            Object.entries(defaults[shape.value]).forEach(([k, dv]) => { if (!inputs[k]) u(k, dv); });
                          }
                        }}
                        className="sr-only" />
                      <div className={`w-10 h-7 mb-1 ${selected ? 'text-[#F59E0B]' : 'text-slate-400'}`}>{shape.icon}</div>
                      <span className={`text-[11px] font-medium leading-tight text-center ${selected ? 'text-[#0F1D32]' : 'text-slate-500'}`}>{shape.label}</span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
            {inputs.pool_shape_template === 'geometric_custom' && (
              <div><Label className="text-xs">Shape Notes</Label><Input value={inputs.pool_shape_notes || ''} onChange={e => u('pool_shape_notes', e.target.value)} placeholder="Describe custom geometry..." className="text-sm" /></div>
            )}
            {/* Live Layout Preview */}
            {/* Enhanced Pool Layout Editor with Sidebar */}
            <PoolDesignEditor inputs={inputs} onUpdate={u} />
          </div>

          {/* Group 2: Dimensions + Depth */}
          <div className="space-y-3">
            <p className="text-[10px] font-bold text-[#F59E0B] uppercase tracking-wider">Dimensions & Depth</p>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Length (ft)</Label><Input type="number" value={inputs.length} onChange={e => u('length', parseFloat(e.target.value) || 0)} data-testid="wizard-length" /></div>
              <div><Label className="text-xs">Width (ft)</Label><Input type="number" value={inputs.width} onChange={e => u('width', parseFloat(e.target.value) || 0)} data-testid="wizard-width" /></div>
              <div><Label className="text-xs flex items-center gap-1">Depth Profile <DepthProfileTooltip /></Label>
                <Select value={inputs.depth_profile_type || 'sport_bottom'} onValueChange={v => u('depth_profile_type', v)}>
                  <SelectTrigger data-testid="wizard-depth-profile"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(DEPTH_PROFILES).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Shallow (ft)</Label><Input type="number" step="0.5" value={inputs.shallow_depth || ''} onChange={e => {
                const v = parseFloat(e.target.value) || 0; u('shallow_depth', v);
                if (!inputs.avg_depth_override && inputs.deep_depth) u('avg_depth', Math.round(((v + (inputs.deep_depth || v)) / 2) * 10) / 10);
              }} placeholder="3.5" /></div>
              <div><Label className="text-xs">Deep (ft)</Label><Input type="number" step="0.5" value={inputs.deep_depth || ''} onChange={e => {
                const v = parseFloat(e.target.value) || 0; u('deep_depth', v);
                if (!inputs.avg_depth_override && inputs.shallow_depth) u('avg_depth', Math.round(((inputs.shallow_depth || v) + v) / 2 * 10) / 10);
              }} placeholder="6" /></div>
              <div><Label className="text-xs flex items-center gap-1">Avg Depth
                {!inputs.avg_depth_override && <span className="text-[8px] text-emerald-600">(auto)</span>}
                {inputs.avg_depth_override && <button className="text-[8px] text-sky-500 underline" onClick={() => {
                  u('avg_depth_override', false);
                  if (inputs.shallow_depth && inputs.deep_depth) u('avg_depth', Math.round(((inputs.shallow_depth + inputs.deep_depth) / 2) * 10) / 10);
                }}>reset</button>}
              </Label><Input type="number" step="0.5" value={inputs.avg_depth} onChange={e => { u('avg_depth', parseFloat(e.target.value) || 0); u('avg_depth_override', true); }} data-testid="wizard-depth" /></div>
            </div>
            <div className="p-3 bg-sky-50 rounded-lg">
              <p className="text-xs text-sky-800 font-medium">Volume: {Math.ceil((inputs.length * inputs.width * inputs.avg_depth) / 27)} CY | Surface: {inputs.length * inputs.width} SF | Perimeter: {2 * (inputs.length + inputs.width)} LF</p>
            </div>
          </div>

          {/* Group 3: Layout + Features */}
          <div className="space-y-3">
            <p className="text-[10px] font-bold text-[#F59E0B] uppercase tracking-wider">Layout & Features</p>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Entry Type</Label>
                <Select value={inputs.entry_type || ''} onValueChange={v => u('entry_type', v)}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(ENTRY_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {inputs.entry_type && ENTRY_TYPES[inputs.entry_type]?.description && <p className="text-[9px] text-slate-400 mt-0.5">{ENTRY_TYPES[inputs.entry_type].description}</p>}
              </div>
              <div><Label className="text-xs">Tanning Ledge</Label>
                <Select value={inputs.tanning_ledge_size || 'none'} onValueChange={v => { u('tanning_ledge_size', v); u('tanning_ledge', v !== 'none'); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TANNING_LEDGES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {inputs.tanning_ledge_size && TANNING_LEDGES[inputs.tanning_ledge_size]?.description && <p className="text-[9px] text-slate-400 mt-0.5">{TANNING_LEDGES[inputs.tanning_ledge_size].description}</p>}
              </div>
              <div><Label className="text-xs">Bench Seating</Label>
                <Select value={inputs.bench_seating || 'none'} onValueChange={v => u('bench_seating', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(BENCH_SEATING).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {inputs.bench_seating && BENCH_SEATING[inputs.bench_seating]?.description && <p className="text-[9px] text-slate-400 mt-0.5">{BENCH_SEATING[inputs.bench_seating].description}</p>}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Slide Placement</Label>
                <Select value={inputs.slide_placement || 'none'} onValueChange={v => { u('slide_placement', v); u('slide', v !== 'none'); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(SLIDE_PLACEMENTS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {inputs.slide_placement && SLIDE_PLACEMENTS[inputs.slide_placement]?.description && <p className="text-[9px] text-slate-400 mt-0.5">{SLIDE_PLACEMENTS[inputs.slide_placement].description}</p>}
              </div>
              <div><Label className="text-xs">Water Features</Label>
                <Select value={inputs.water_feature_type || 'none'} onValueChange={v => { u('water_feature_type', v); u('waterfall', v !== 'none'); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(WATER_FEATURES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {inputs.water_feature_type && WATER_FEATURES[inputs.water_feature_type]?.description && <p className="text-[9px] text-slate-400 mt-0.5">{WATER_FEATURES[inputs.water_feature_type].description}</p>}
              </div>
              <div><Label className="text-xs">Fire Features</Label>
                <Select value={inputs.fire_feature_type || 'none'} onValueChange={v => { u('fire_feature_type', v); u('fire_features', v !== 'none'); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(FIRE_FEATURES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {inputs.fire_feature_type && FIRE_FEATURES[inputs.fire_feature_type]?.description && <p className="text-[9px] text-slate-400 mt-0.5">{FIRE_FEATURES[inputs.fire_feature_type].description}</p>}
              </div>
            </div>
          </div>

          {/* Group 4: Spa (conditional) + Orientation */}
          <div className="space-y-3">
            <p className="text-[10px] font-bold text-[#F59E0B] uppercase tracking-wider">Spa & Site</p>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Spa</Label>
                <Select value={inputs.spa_type || 'none'} onValueChange={v => { u('spa_type', v); u('spa', v !== 'none'); }}>
                  <SelectTrigger data-testid="wizard-spa-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Spa</SelectItem>
                    <SelectItem value="attached">Attached Spa</SelectItem>
                    <SelectItem value="detached">Detached Spa</SelectItem>
                  </SelectContent>
                </Select></div>
              {(inputs.spa_type && inputs.spa_type !== 'none') && (
                <>
                  <div><Label className="text-xs">Spa Shape</Label>
                    <Select value={inputs.spa_shape || ''} onValueChange={v => u('spa_shape', v)}>
                      <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="square">Square</SelectItem>
                        <SelectItem value="rectangle">Rectangle</SelectItem>
                        <SelectItem value="round">Round</SelectItem>
                        <SelectItem value="freeform">Freeform</SelectItem>
                      </SelectContent>
                    </Select></div>
                  <div><Label className="text-xs">Spa Location</Label>
                    <Select value={inputs.spa_location || ''} onValueChange={v => u('spa_location', v)}>
                      <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="left">Left</SelectItem>
                        <SelectItem value="right">Right</SelectItem>
                        <SelectItem value="center">Center</SelectItem>
                        <SelectItem value="custom">Custom</SelectItem>
                      </SelectContent>
                    </Select></div>
                </>
              )}
            </div>
            {(inputs.spa_type && inputs.spa_type !== 'none') && (
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs">Spillway</Label>
                  <Select value={inputs.spa_spillway || 'none'} onValueChange={v => u('spa_spillway', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="spillover">Spillover</SelectItem>
                      <SelectItem value="raised_wall">Raised Wall Spillway</SelectItem>
                    </SelectContent>
                  </Select></div>
              </div>
            )}
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Orientation to House</Label>
                <Select value={inputs.pool_orientation || ''} onValueChange={v => u('pool_orientation', v)}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="parallel">Parallel</SelectItem>
                    <SelectItem value="perpendicular">Perpendicular</SelectItem>
                    <SelectItem value="angled">Angled</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select></div>
              <div><Label className="text-xs">Yard Condition</Label>
                <Select value={inputs.yard_condition || 'flat'} onValueChange={v => u('yard_condition', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="flat">Flat</SelectItem>
                    <SelectItem value="mild_slope">Mild Slope</SelectItem>
                    <SelectItem value="significant_slope">Significant Slope</SelectItem>
                  </SelectContent>
                </Select></div>
              <div><Label className="text-xs">View / Edge</Label>
                <Select value={inputs.view_condition || 'standard'} onValueChange={v => u('view_condition', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard Yard</SelectItem>
                    <SelectItem value="view_lot">View Lot</SelectItem>
                    <SelectItem value="tight_courtyard">Tight / Courtyard</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select></div>
            </div>
          </div>

          {/* Design Notes */}
          <div>
            <Label className="text-xs">Pool Design Notes</Label>
            <Textarea value={inputs.pool_design_notes || ''} onChange={e => u('pool_design_notes', e.target.value)} rows={2} className="text-sm mt-1"
              placeholder="e.g. Spa on left near patio, modern rectangle with tanning ledge on shallow side..." data-testid="wizard-design-notes" />
          </div>
        </div>
      );
      case 2: return (
        <div className="space-y-4 animate-fade-in">
          <h2 className="text-lg font-bold text-slate-900">Finish & Decking</h2>
          <div>
            <Label>Interior Finish</Label>
            <Select value={inputs.finish_type} onValueChange={v => u('finish_type', v)}>
              <SelectTrigger data-testid="wizard-finish"><SelectValue placeholder="Select..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="White Plaster">White Plaster ($5/SF)</SelectItem>
                <SelectItem value="Pebble Tec">Pebble Tec ($10/SF)</SelectItem>
                <SelectItem value="QuartzScapes">QuartzScapes ($12/SF)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Tile Type</Label>
              <Select value={inputs.tile_type} onValueChange={v => u('tile_type', v)}>
                <SelectTrigger data-testid="wizard-tile"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Standard">Standard Ceramic ($18/LF)</SelectItem>
                  <SelectItem value="Glass Mosaic">Glass Mosaic ($35/LF)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Coping</Label>
              <Select value={inputs.coping_type} onValueChange={v => u('coping_type', v)}>
                <SelectTrigger data-testid="wizard-coping"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Bullnose">Bullnose ($22/LF)</SelectItem>
                  <SelectItem value="Natural Stone">Natural Stone ($38/LF)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {/* Multiple Deck Entries */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-bold text-slate-700">Decking</Label>
              <Button size="sm" variant="outline" onClick={() => {
                const entries = [...(inputs.deck_entries || []), { material: 'Concrete Broom Finish', sqft: 0 }];
                u('deck_entries', entries);
              }} data-testid="add-deck-entry-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Deck Area
              </Button>
            </div>
            {(inputs.deck_entries || []).map((de, idx) => (
              <div key={idx} className="grid grid-cols-[1fr_1fr_auto] gap-2 mb-2 items-center">
                <Input type="number" value={de.sqft || ''} placeholder="Square feet"
                  onChange={e => {
                    const entries = [...(inputs.deck_entries || [])];
                    entries[idx] = { ...entries[idx], sqft: parseFloat(e.target.value) || 0 };
                    u('deck_entries', entries);
                  }} data-testid={`deck-sqft-${idx}`} />
                <Select value={de.material} onValueChange={v => {
                  const entries = [...(inputs.deck_entries || [])];
                  entries[idx] = { ...entries[idx], material: v };
                  u('deck_entries', entries);
                }}>
                  <SelectTrigger data-testid={`deck-material-${idx}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Concrete Broom Finish">Concrete Broom ($12/SF)</SelectItem>
                    <SelectItem value="Kool Deck">Kool Deck ($10/SF)</SelectItem>
                    <SelectItem value="Stamped Concrete">Stamped Concrete ($16/SF)</SelectItem>
                    <SelectItem value="Pavers - Standard">Pavers ($20/SF)</SelectItem>
                    <SelectItem value="Travertine Pavers">Travertine ($27/SF)</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400" onClick={() => {
                  const entries = [...(inputs.deck_entries || [])]; entries.splice(idx, 1); u('deck_entries', entries);
                }}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            ))}
            {(inputs.deck_entries || []).length === 0 && (
              <p className="text-xs text-slate-400 italic">No decking added yet. Click "Add Deck Area" to include.</p>
            )}
          </div>
        </div>
      );
      case 3: return (
        <div className="space-y-5 animate-fade-in">
          <h2 className="text-lg font-bold text-[#1e3a50]">Equipment & Utilities</h2>

          {/* Pentair Package */}
          <div className="p-4 bg-[#e4f0f6] border border-[#c8dce8] rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <Label className="text-[#2a6496] font-bold text-sm">HDH Standard — Pentair Equipment</Label>
              <Badge className="bg-[#4a9ac5]/15 text-[#2a6496] text-[9px] font-bold">PREMIUM STANDARD</Badge>
            </div>
            <p className="text-[11px] font-medium text-[#4a6a7e] mb-3">All HDH pools include Pentair equipment as standard. Select your package below.</p>
            <Select value={inputs.equipment_package} onValueChange={v => u('equipment_package', v)}>
              <SelectTrigger className="bg-white" data-testid="wizard-equipment-package"><SelectValue placeholder="Select a package..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="basic">Pentair Individual — Pump + Filter + Heater + Salt + Automation (~$16,950)</SelectItem>
                <SelectItem value="mid">Pentair Pool Package — Premium Package + 3 Pool + 1 Spa Light + Pad (~$23,000)</SelectItem>
                <SelectItem value="premium">Pentair Pool + Spa Package — Premium Spa Package + Lights + Pad (~$27,000)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Additional equipment options */}
          <div className="grid grid-cols-3 gap-4">
            <div><Label className="text-xs font-bold">Pool Lights</Label><Input type="number" min="0" value={inputs.lights_count} onChange={e => u('lights_count', parseInt(e.target.value) || 0)} data-testid="wizard-lights" /></div>
            <div><Label className="text-xs font-bold">Main Drains</Label><Input type="number" min="1" value={inputs.drains} onChange={e => u('drains', parseInt(e.target.value) || 1)} data-testid="wizard-drains" /></div>
            <div><Label className="text-xs font-bold">Skimmers</Label><Input type="number" min="1" value={inputs.skimmers} onChange={e => u('skimmers', parseInt(e.target.value) || 1)} data-testid="wizard-skimmers" /></div>
          </div>

          {/* Utility Runs — 2 Card Layout */}
          <div className="space-y-3">
            {/* CARD 1 — Included */}
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-sm">
              <p className="text-sm font-bold text-emerald-800 mb-2" style={{fontFamily:'Manrope',fontStyle:'normal'}}>Included Utility Runs</p>
              <ul className="text-xs text-emerald-700 space-y-1 mb-2" style={{fontFamily:'Manrope',fontStyle:'normal'}}>
                <li>Electric run from equipment pad to home connection — up to <strong>{inputs.electric_included_ft} ft</strong> INCLUDED.</li>
                <li>Gas run from equipment pad to home connection — up to <strong>{inputs.gas_included_ft} ft</strong> INCLUDED.</li>
              </ul>
              <p className="text-[10px] text-emerald-600 italic" style={{fontFamily:'Manrope',fontStyle:'normal'}}>Most builders stop at the equipment pad. HDH includes utility runs to your home's connection point up to the distances shown above.</p>
            </div>

            {/* CARD 2 — Additional */}
            <div className="p-4 bg-white border border-slate-200 rounded-sm">
              <p className="text-sm font-bold text-[#0F1D32] mb-2" style={{fontFamily:'Manrope',fontStyle:'normal'}}>Additional Utility Runs (If Needed)</p>
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-amber-500">Electric Distance (ft)</Label>
                  <Input type="number" min="0" value={inputs.electric_distance} onChange={e => u('electric_distance', parseInt(e.target.value) || 0)}
                    className="rounded-sm" data-testid="wizard-electric-dist" />
                </div>
                <div>
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-amber-500">Gas Distance (ft)</Label>
                  <Input type="number" min="0" value={inputs.gas_distance} onChange={e => u('gas_distance', parseInt(e.target.value) || 0)}
                    className="rounded-sm" data-testid="wizard-gas-dist" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div className="p-2.5 bg-[#F8F6F3] rounded-sm">
                  {inputs.electric_distance <= inputs.electric_included_ft ? (
                    <p className="text-xs font-bold text-emerald-600">Electric: Within included — no extra</p>
                  ) : (
                    <p className="text-xs font-bold text-amber-600">Electric: +{inputs.electric_distance - inputs.electric_included_ft} ft @ $18/ft = <span className="font-mono">${(inputs.electric_distance - inputs.electric_included_ft) * 18}</span></p>
                  )}
                </div>
                <div className="p-2.5 bg-[#F8F6F3] rounded-sm">
                  {inputs.gas_distance <= inputs.gas_included_ft ? (
                    <p className="text-xs font-bold text-emerald-600">Gas: Within included — no extra</p>
                  ) : (
                    <p className="text-xs font-bold text-amber-600">Gas: +{inputs.gas_distance - inputs.gas_included_ft} ft @ $24/ft = <span className="font-mono">${(inputs.gas_distance - inputs.gas_included_ft) * 24}</span></p>
                  )}
                </div>
              </div>
              <p className="text-[10px] text-slate-500 italic" style={{fontFamily:'Manrope',fontStyle:'normal'}}>If your home's gas or electrical connection is farther than {inputs.electric_included_ft} ft, we only charge for the extra distance beyond what's included. Final distances are confirmed during your site visit so there are no surprises.</p>
            </div>

            {/* Upgrades */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-3">
                <Switch checked={inputs.panel_upgrade} onCheckedChange={v => u('panel_upgrade', v)} data-testid="wizard-panel-upgrade" />
                <div><Label className="text-xs font-bold" style={{fontFamily:'Manrope',fontStyle:'normal'}}>Electrical Panel Upgrade (200A) — +$3,500</Label></div>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={inputs.gas_meter_upgrade} onCheckedChange={v => u('gas_meter_upgrade', v)} data-testid="wizard-gas-meter" />
                <div><Label className="text-xs font-bold" style={{fontFamily:'Manrope',fontStyle:'normal'}}>Gas Meter Upgrade — +$2,500</Label></div>
              </div>
            </div>
          </div>
        </div>
      );
      case 4: return (
        <div className="space-y-4 animate-fade-in">
          <h2 className="text-lg font-bold text-slate-900">Features</h2>
          <div className="grid grid-cols-2 gap-3">
            {[
              { key: 'spa', label: 'Spa / Hot Tub', desc: 'Attached spa with spillover' },
              { key: 'waterfall', label: 'Waterfall', desc: 'Natural rock waterfall' },
              { key: 'slide', label: 'Pool Slide', desc: 'Fiberglass slide' },
              { key: 'tanning_ledge', label: 'Tanning Ledge', desc: 'Sun shelf / Baja step' },
              { key: 'fire_features', label: 'Fire Features', desc: 'Fire bowls (2)' },
              { key: 'retaining_wall', label: 'Retaining Wall', desc: 'CUSTOM BID ONLY - separately engineered' },
            ].map(f => (
              <Card key={f.key} className={`cursor-pointer transition-all border-2 ${inputs[f.key] ? 'border-sky-400 bg-sky-50' : 'border-slate-100 hover:border-slate-200'}`}
                onClick={() => u(f.key, !inputs[f.key])} data-testid={`wizard-feature-${f.key}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{f.label}</p>
                      <p className="text-xs text-slate-500">{f.desc}</p>
                    </div>
                    {inputs[f.key] && <Check className="w-5 h-5 text-sky-500" />}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pool Cover */}
          <div className="p-4 bg-white border border-[#d4e6ef] rounded-xl">
            <Label className="text-[#1e3a50] font-bold text-sm mb-2 block">Pool Cover</Label>
            <Select value={inputs.cover_type} onValueChange={v => u('cover_type', v)}>
              <SelectTrigger className="bg-white" data-testid="wizard-cover-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Cover</SelectItem>
                <SelectItem value="winter">Winter / Tarp Cover (manual) — $750</SelectItem>
                <SelectItem value="solar">Solar Cover + Reel — $1,500</SelectItem>
                <SelectItem value="safety_mesh">Manual Safety Cover (mesh) — $3,800</SelectItem>
                <SelectItem value="safety_solid">Manual Safety Cover (solid) — $4,200</SelectItem>
                <SelectItem value="auto">Automatic Safety Cover — $18,000+</SelectItem>
                <SelectItem value="auto_complex">Automatic Safety Cover (complex/large) — $22,000+</SelectItem>
                <SelectItem value="sliding_deck">Sliding Deck / Hard-Top System — $50,000+</SelectItem>
              </SelectContent>
            </Select>
            {inputs.cover_type !== 'none' && (
              <div className="mt-3 space-y-2">
                <div>
                  <Label className="text-[10px] font-bold text-[#6b8a9e] uppercase">Pool Size / Complexity</Label>
                  <Select value={inputs.cover_size} onValueChange={v => u('cover_size', v)}>
                    <SelectTrigger className="h-8 text-xs" data-testid="wizard-cover-size"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="small">Small (under 400sf) — 0.85x</SelectItem>
                      <SelectItem value="standard">Standard (400-600sf) — 1.0x</SelectItem>
                      <SelectItem value="large">Large (600-800sf) — 1.15x</SelectItem>
                      <SelectItem value="complex">Complex Shape / Oversized — 1.30x</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {inputs.cover_type === 'auto' && (
                  <p className="text-[10px] font-medium text-[#4a6a7e] bg-[#f5f9fb] p-2 rounded">Price includes system, track, and standard rectangular installation. Complex designs or lids may require additional pricing.</p>
                )}
              </div>
            )}
          </div>

          {/* Custom Items (Landscaping, Outdoor Living, etc.) */}
          <div className="pt-2">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="text-sm font-bold text-slate-700">Custom Items</h3>
                <p className="text-[10px] text-slate-400">Landscaping, outdoor kitchen, pergola, fencing, or any custom scope</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => { setCustomFeature({ name: '', description: '', cost: 0, sell_price: 0, category: 'custom' }); setCustomFeatureOpen(true); }}
                data-testid="add-custom-feature-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Custom Item
              </Button>
            </div>
            {inputs.custom_features.length > 0 && (
              <div className="space-y-2">
                {inputs.custom_features.map((cf, idx) => (
                  <Card key={idx} className="border-orange-200 bg-orange-50/50">
                    <CardContent className="p-3 flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-slate-800">{cf.name}</span>
                          {cf.category && cf.category !== 'custom' && (
                            <Badge variant="outline" className="text-[9px] capitalize">{cf.category}</Badge>
                          )}
                        </div>
                        {cf.description && <p className="text-xs text-slate-500 mt-0.5">{cf.description}</p>}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-mono font-bold text-slate-800">${parseFloat(cf.sell_price || 0).toLocaleString()}</span>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={() => {
                          const cfs = [...inputs.custom_features]; cfs.splice(idx, 1);
                          u('custom_features', cfs);
                        }} data-testid={`remove-custom-${idx}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      );
      case 5: return (
        <div className="space-y-4 animate-fade-in">
          <h2 className="text-lg font-bold text-slate-900">Site Difficulty</h2>
          <div>
            <Label>Access Difficulty</Label>
            <Select value={inputs.access_difficulty} onValueChange={v => u('access_difficulty', v)}>
              <SelectTrigger data-testid="wizard-access"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="standard">Standard - Easy truck/equipment access</SelectItem>
                <SelectItem value="moderate">Moderate - Tight access, some hand work</SelectItem>
                <SelectItem value="difficult">Difficult - Crane may be needed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Soil / Rock Risk</Label>
            <Select value={inputs.soil_rock_risk} onValueChange={v => u('soil_rock_risk', v)}>
              <SelectTrigger data-testid="wizard-soil"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low - Standard soil</SelectItem>
                <SelectItem value="medium">Medium - Some caliche/clay</SelectItem>
                <SelectItem value="high">High - Known rock/hardpan</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <Switch checked={inputs.rock_hard_dig} onCheckedChange={v => u('rock_hard_dig', v)} data-testid="wizard-rock-dig" />
            <div>
              <Label className="text-amber-800 font-semibold">Rock / Hard Dig Contingency</Label>
              <p className="text-xs text-amber-700">Adds 5-15% contingency on excavation subtotal</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={inputs.crane_needed} onCheckedChange={v => u('crane_needed', v)} data-testid="wizard-crane" />
            <Label>Crane Needed</Label>
          </div>
        </div>
      );
      case 6: return (
        <div className="space-y-4 animate-fade-in">
          <h2 className="text-lg font-bold text-slate-900">Permits & Engineering</h2>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>City Permit</Label>
              <Button variant="ghost" size="sm" className="text-sky-600 text-xs h-7 px-2"
                onClick={() => setAddCityOpen(true)} data-testid="wizard-add-city-btn">
                <Plus className="w-3 h-3 mr-1" /> Add City
              </Button>
            </div>
            <Select value={inputs.city_permit} onValueChange={v => u('city_permit', v)}>
              <SelectTrigger data-testid="wizard-city-permit"><SelectValue placeholder="Select city..." /></SelectTrigger>
              <SelectContent>
                {permitCities.map(c => (
                  <SelectItem key={c.city} value={c.city}>{c.city} (${c.cost.toLocaleString()})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {inputs.city_permit && (
              <p className="text-xs text-slate-500 mt-1">
                Permit fee: ${permitCities.find(c => c.city === inputs.city_permit)?.cost?.toLocaleString() || '—'}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={inputs.structural_engineering} onCheckedChange={v => u('structural_engineering', v)} data-testid="wizard-engineering" />
            <Label>Structural Engineering Required ($1,500)</Label>
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={inputs.bond_required} onCheckedChange={v => u('bond_required', v)} data-testid="wizard-bond" />
            <Label>Contractor Bond ($500)</Label>
          </div>
        </div>
      );
      case 7: return (
        <div className="space-y-4 animate-fade-in">
          <h2 className="text-lg font-bold text-slate-900">{isEditMode ? 'Review & Save Changes' : 'Review & Create Bid'}</h2>
          <div className="grid grid-cols-2 gap-4">
            <Card className="border-slate-100"><CardContent className="p-4">
              <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Customer</p>
              <p className="text-sm font-bold text-slate-800">{inputs.customer_name || 'Not set'}</p>
              <p className="text-xs text-slate-500">{inputs.address}, {inputs.city}</p>
            </CardContent></Card>
            <Card className="border-slate-100"><CardContent className="p-4">
              <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Pool</p>
              <p className="text-sm font-bold text-slate-800 capitalize">{inputs.pool_type} - {inputs.length}x{inputs.width}</p>
              <p className="text-xs text-slate-500">Avg depth: {inputs.avg_depth}ft</p>
            </CardContent></Card>
            <Card className="border-slate-100"><CardContent className="p-4">
              <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Finish</p>
              <p className="text-sm text-slate-800">{inputs.finish_type || 'Not set'}</p>
              <p className="text-xs text-slate-500">{inputs.tile_type} tile, {inputs.coping_type} coping</p>
            </CardContent></Card>
            <Card className="border-slate-100"><CardContent className="p-4">
              <p className="text-xs text-[#6b8a9e] uppercase font-bold mb-1">Equipment</p>
              <p className="text-sm font-bold text-[#1e3a50] capitalize">Pentair {inputs.equipment_package || 'Standard'}</p>
              <p className="text-xs font-medium text-[#6b8a9e]">{inputs.lights_count} lights</p>
            </CardContent></Card>
          </div>
          {/* Utility Summary */}
          <Card className="border-[#c8dce8] bg-[#f5f9fb]"><CardContent className="p-4">
            <p className="text-xs text-[#2a6496] uppercase font-bold mb-2">Utility Runs (Included in Bid)</p>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="font-bold text-[#1e3a50]">Electric:</span>{' '}
                <span className="font-medium text-[#4a6a7e]">{inputs.electric_distance}ft ({inputs.electric_included_ft}ft included{inputs.electric_distance > inputs.electric_included_ft ? `, +${inputs.electric_distance - inputs.electric_included_ft}ft extra` : ''})</span>
              </div>
              <div>
                <span className="font-bold text-[#1e3a50]">Gas:</span>{' '}
                <span className="font-medium text-[#4a6a7e]">{inputs.gas_distance}ft ({inputs.gas_included_ft}ft included{inputs.gas_distance > inputs.gas_included_ft ? `, +${inputs.gas_distance - inputs.gas_included_ft}ft extra` : ''})</span>
              </div>
            </div>
            {(inputs.panel_upgrade || inputs.gas_meter_upgrade || inputs.shared_trench) && (
              <div className="flex gap-2 mt-2">
                {inputs.panel_upgrade && <Badge className="bg-amber-100 text-amber-700 text-[9px]">Panel Upgrade</Badge>}
                {inputs.gas_meter_upgrade && <Badge className="bg-amber-100 text-amber-700 text-[9px]">Gas Meter Upgrade</Badge>}
                {inputs.shared_trench && <Badge className="bg-emerald-100 text-emerald-700 text-[9px]">Shared Trench</Badge>}
              </div>
            )}
          </CardContent></Card>
          <div className="flex flex-wrap gap-2">
            {inputs.spa && <Badge className="bg-sky-100 text-sky-700">Spa</Badge>}
            {inputs.waterfall && <Badge className="bg-sky-100 text-sky-700">Waterfall</Badge>}
            {inputs.slide && <Badge className="bg-sky-100 text-sky-700">Slide</Badge>}
            {inputs.tanning_ledge && <Badge className="bg-sky-100 text-sky-700">Tanning Ledge</Badge>}
            {inputs.fire_features && <Badge className="bg-sky-100 text-sky-700">Fire Features</Badge>}
            {inputs.rock_hard_dig && <Badge className="bg-amber-100 text-amber-700">Rock Dig</Badge>}
            {inputs.structural_engineering && <Badge className="bg-violet-100 text-violet-700">Engineering</Badge>}
            {inputs.city_permit && <Badge className="bg-emerald-100 text-emerald-700">{inputs.city_permit} Permit</Badge>}
            {inputs.cover_type && inputs.cover_type !== 'none' && (
              <Badge className="bg-cyan-100 text-cyan-700">
                {inputs.cover_type === 'auto' ? 'Auto Cover' : inputs.cover_type === 'auto_complex' ? 'Auto Cover (complex)' : inputs.cover_type === 'sliding_deck' ? 'Sliding Deck' : inputs.cover_type === 'solar' ? 'Solar Cover' : inputs.cover_type === 'safety_mesh' ? 'Safety Cover (mesh)' : inputs.cover_type === 'safety_solid' ? 'Safety Cover (solid)' : inputs.cover_type === 'winter' ? 'Winter Cover' : 'Cover'}
              </Badge>
            )}
          </div>
          <div className="p-4 bg-sky-50 rounded-xl">
            <p className="text-sm text-sky-800">Line items will be auto-populated from the pricing library based on your selections. You can edit them after creation.</p>
          </div>
        </div>
      );
      default: return null;
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto" data-testid="bid-wizard-page">
      <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mb-6">{isEditMode ? 'Edit Proposal' : 'New Bid'}</h1>
      <div className="flex gap-8">
        <div className="hidden md:block w-56 flex-shrink-0">
          <div className="space-y-1 sticky top-24">
            {STEPS.map((s, i) => (
              <button key={i} onClick={() => setStep(i)} data-testid={`wizard-step-${i}`}
                className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm text-left transition-all ${
                  i === step ? 'bg-sky-50 text-sky-700 font-semibold' :
                  i < step ? 'text-emerald-600' : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                  i === step ? 'bg-sky-500 text-white' :
                  i < step ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'
                }`}>
                  {i < step ? <Check className="w-3.5 h-3.5" /> : <s.icon className="w-3.5 h-3.5" />}
                </div>
                <span>{s.label}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1">
          <Card className="border-slate-100 mb-4">
            <CardContent className="p-6">{renderStep()}</CardContent>
          </Card>
          <div className="flex justify-between">
            <Button variant="outline" disabled={step === 0} onClick={() => setStep(s => s - 1)} data-testid="wizard-prev-btn">
              <ChevronLeft className="w-4 h-4 mr-1" /> Previous
            </Button>
            {step < 7 ? (
              <Button className="bg-sky-500 hover:bg-sky-600 text-white" onClick={() => setStep(s => s + 1)} data-testid="wizard-next-btn">
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button className="bg-orange-500 hover:bg-orange-600 text-white px-6" onClick={handleCreate} disabled={creating || !inputs.customer_name}
                data-testid="wizard-create-bid-btn">
                {creating ? (isEditMode ? 'Saving...' : 'Creating...') : (isEditMode ? 'Save Changes & Return' : 'Create Bid & Open Editor')}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Add City Permit Dialog */}
      <Dialog open={addCityOpen} onOpenChange={setAddCityOpen}>
        <DialogContent className="max-w-sm" data-testid="add-city-dialog">
          <DialogHeader>
            <DialogTitle>Add City Permit</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>City Name *</Label>
              <Input
                value={newCity.city}
                onChange={e => setNewCity(p => ({ ...p, city: e.target.value }))}
                placeholder="e.g. Draper, Riverton, Herriman..."
                data-testid="add-city-name-input"
              />
            </div>
            <div>
              <Label>Permit Fee ($)</Label>
              <Input
                type="number"
                step="50"
                value={newCity.cost}
                onChange={e => setNewCity(p => ({ ...p, cost: e.target.value }))}
                data-testid="add-city-cost-input"
              />
              <p className="text-xs text-slate-400 mt-1">This will be added to your Pricing Library under Permits</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddCityOpen(false)}>Cancel</Button>
            <Button
              className="bg-sky-500 hover:bg-sky-600 text-white"
              onClick={handleAddCity}
              disabled={!newCity.city.trim()}
              data-testid="add-city-save-btn"
            >
              Add City
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Feature Dialog */}
      <Dialog open={customFeatureOpen} onOpenChange={setCustomFeatureOpen}>
        <DialogContent className="max-w-sm" data-testid="custom-feature-dialog">
          <DialogHeader>
            <DialogTitle>Add Custom Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Category</Label>
              <Select value={customFeature.category || 'custom'} onValueChange={v => setCustomFeature(p => ({...p, category: v}))}>
                <SelectTrigger data-testid="custom-feature-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="custom">Custom / Other</SelectItem>
                  <SelectItem value="landscaping">Landscaping</SelectItem>
                  <SelectItem value="outdoor_living">Outdoor Living</SelectItem>
                  <SelectItem value="fencing">Fencing / Gates</SelectItem>
                  <SelectItem value="lighting">Landscape Lighting</SelectItem>
                  <SelectItem value="hardscape">Hardscape</SelectItem>
                  <SelectItem value="features">Pool Features</SelectItem>
                  <SelectItem value="misc">Miscellaneous</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Item Name *</Label>
              <Input value={customFeature.name} onChange={e => setCustomFeature(p => ({...p, name: e.target.value}))}
                placeholder="e.g. Front Yard Landscaping, Pergola, BBQ Island..." data-testid="custom-feature-name" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={customFeature.description} onChange={e => setCustomFeature(p => ({...p, description: e.target.value}))}
                placeholder="Scope details..." rows={2} data-testid="custom-feature-desc" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Your Cost ($)</Label>
                <Input type="number" step="100" value={customFeature.cost}
                  onChange={e => setCustomFeature(p => ({...p, cost: e.target.value}))} data-testid="custom-feature-cost" />
              </div>
              <div>
                <Label>Sell Price ($)</Label>
                <Input type="number" step="100" value={customFeature.sell_price}
                  onChange={e => setCustomFeature(p => ({...p, sell_price: e.target.value}))} data-testid="custom-feature-sell" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCustomFeatureOpen(false)}>Cancel</Button>
            <Button className="bg-orange-500 hover:bg-orange-600 text-white" disabled={!customFeature.name.trim()}
              onClick={() => {
                u('custom_features', [...inputs.custom_features, { ...customFeature }]);
                setCustomFeatureOpen(false);
                toast.success('Custom item added');
              }} data-testid="custom-feature-save-btn">
              Add to Bid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ─── Plan-Style Pool Layout Preview ───

// Pool body paths scaled for 240x170 viewBox (pool area: x30-210, y30-145)




function PoolLayoutEditor({ inputs, onUpdate, layout }) {
  const [placingFeature, setPlacingFeature] = useState(null);
  const shape = inputs.pool_shape_template;
  const poolPath = layout ? generatePoolPath(shape, layout.poolW, layout.poolH, layout.ox, layout.oy) : POOL_PLAN_PATHS[shape];
  const sd = shape ? (layout ? getShapeData(shape, layout.lengthFt, layout.widthFt) : getShapeData(shape)) : null;

  // Feature states
  const hasSpa = inputs.spa_type && inputs.spa_type !== 'none';
  const hasLedge = inputs.tanning_ledge_size && inputs.tanning_ledge_size !== 'none';
  const hasSlide = inputs.slide_placement && inputs.slide_placement !== 'none';
  const hasBench = inputs.bench_seating && inputs.bench_seating !== 'none';
  const hasSteps = inputs.entry_type && inputs.entry_type !== 'no_steps' && inputs.entry_type;
  const isAutoCover = shape === 'auto_cover_rectangle';
  const hasWater = inputs.water_feature_type && inputs.water_feature_type !== 'none';
  const hasFire = inputs.fire_feature_type && inputs.fire_feature_type !== 'none';

  // Colors
  const C = {
    deck: '#D5CCBF', deckStroke: '#BFB5A5',
    pool: '#7ABFDB', poolStroke: '#2D6682',
    steps: '#B8BFC8', stepsStroke: '#6B7A8A',
    ledge: '#7DC8BA', ledgeStroke: '#4A9E8F',
    bench: '#AEA599', benchStroke: '#8C8278',
    spa: '#5AADCF', spaStroke: '#2A7A9E',
    slide: '#CF8420', slideStroke: '#9E5F10',
    water: '#4A8FD4', waterStroke: '#2E6BA8',
    fire: '#C44B30', fireCore: '#E8863A',
    cover: '#5C6670', coverStroke: '#3D4852',
    zoneHighlight: '#F59E0B', zoneFill: 'rgba(245,158,11,0.15)',
  };

  const zones = placingFeature ? getPlacementZones(shape, placingFeature) : [];

  const handleZoneClick = (zone) => {
    if (!placingFeature) return;
    if (placingFeature === 'entry_type') {
      onUpdate('entry_type', zone.id);
    } else if (placingFeature === 'tanning_ledge_size') {
      onUpdate('tanning_ledge_size', zone.id);
      onUpdate('tanning_ledge', true);
    } else if (placingFeature === 'bench_seating') {
      onUpdate('bench_seating', zone.id);
    } else if (placingFeature === 'spa_location') {
      onUpdate('spa_location', zone.id);
      if (!inputs.spa_type || inputs.spa_type === 'none') onUpdate('spa_type', 'attached');
      if (!inputs.spa_shape) onUpdate('spa_shape', 'round');
      onUpdate('spa', true);
    }
    setPlacingFeature(null);
  };

  const removeFeature = (feature) => {
    if (feature === 'entry_type') onUpdate('entry_type', 'no_steps');
    else if (feature === 'tanning_ledge_size') { onUpdate('tanning_ledge_size', 'none'); onUpdate('tanning_ledge', false); }
    else if (feature === 'bench_seating') onUpdate('bench_seating', 'none');
    else if (feature === 'spa_location') { onUpdate('spa_type', 'none'); onUpdate('spa', false); }
  };

  if (!shape || !poolPath || !sd) {
    return (
      <div className="border-2 border-dashed border-slate-200 rounded-xl text-center bg-slate-50/50 p-8" data-testid="pool-layout-editor">
        <p className="text-sm text-slate-400 font-medium">Select a pool shape above to open the layout editor</p>
      </div>
    );
  }

  // Anchor references for rendering placed features
  const se = sd.shallowEdge;
  const lw = sd.leftWall;
  const rw = sd.rightWall;
  const de = sd.deepEdge;
  const lz = sd.ledgeZone;
  const spaA = sd.spaAttach[inputs.spa_location] || sd.spaAttach.right;
  const slPos = (sd.slidePos || {})[inputs.slide_placement] || (sd.slidePos || {}).deep_end;
  const ww = sd.waterWall;
  const stepsW = se.x2 - se.x1;
  const stepsMidX = (se.x1 + se.x2) / 2;
  const ledgeH = inputs.tanning_ledge_size === 'large' ? lz.maxH : inputs.tanning_ledge_size === 'medium' ? lz.maxH * 0.78 : lz.maxH * 0.52;
  const ledgeW = inputs.tanning_ledge_size === 'large' ? lz.w * 0.88 : inputs.tanning_ledge_size === 'medium' ? lz.w * 0.62 : lz.w * 0.36;
  const ledgeX = inputs.tanning_ledge_size === 'large' ? lz.x + (lz.w - ledgeW) / 2 : lz.x + lz.w - ledgeW - 4;

  return (
    <div className="w-full h-full" data-testid="pool-layout-editor">
      {/* Canvas */}
      <div style={{height: '100%', background: 'transparent'}} className="relative">
        <svg viewBox={layout ? `0 0 ${layout.viewW} ${layout.viewH}` : '0 0 260 190'} className="w-full h-full" style={{maxWidth: layout ? `${Math.min(960, Math.round(layout.viewW / layout.viewH * 500))}px` : '600px', display:'block', margin:'0 auto'}}>
          {/* Deck */}
          <rect x="3" y="3" width={layout ? layout.viewW - 6 : 254} height={layout ? layout.viewH - 6 : 184} rx="5" fill={C.deck} stroke={C.deckStroke} strokeWidth="0.8" />

          {/* Pool body */}
          <path d={poolPath} fill={C.pool} stroke={C.poolStroke} strokeWidth="2.8" strokeLinejoin="round" />
          {shape === 'negative_edge' && <line x1={de.x1} y1={de.y1} x2={de.x2} y2={de.y2} stroke={C.poolStroke} strokeWidth="6" />}

          {/* Auto cover */}
          {isAutoCover && sd.coverHousing && <>
            <rect x={sd.coverHousing.x} y={sd.coverHousing.y} width={sd.coverHousing.w} height={sd.coverHousing.h} rx="2" fill={C.cover} stroke={C.coverStroke} strokeWidth="1.2" />
            {sd.coverTrackL && <line x1={sd.coverTrackL.x} y1={sd.coverTrackL.y1} x2={sd.coverTrackL.x} y2={sd.coverTrackL.y2} stroke={C.cover} strokeWidth="1.5" strokeDasharray="5 3" opacity="0.4" />}
            {sd.coverTrackR && <line x1={sd.coverTrackR.x} y1={sd.coverTrackR.y1} x2={sd.coverTrackR.x} y2={sd.coverTrackR.y2} stroke={C.cover} strokeWidth="1.5" strokeDasharray="5 3" opacity="0.4" />}
          </>}

          {/* Placed features — Steps */}
          {inputs.entry_type === 'full_width_steps' && <rect x={se.x1 + 2} y={se.y1} width={stepsW - 4} height="26" rx="1" fill={C.steps} stroke={C.stepsStroke} strokeWidth="2" />}
          {inputs.entry_type === 'corner_steps' && <path d={`M ${se.x1} ${se.y1} L ${se.x1 + 40} ${se.y1} L ${se.x1 + 40} ${se.y1 + 28} L ${se.x1 + 28} ${se.y1 + 28} L ${se.x1 + 28} ${se.y1 + 19} L ${se.x1 + 15} ${se.y1 + 19} L ${se.x1 + 15} ${se.y1 + 10} L ${se.x1} ${se.y1 + 10} Z`} fill={C.steps} stroke={C.stepsStroke} strokeWidth="2" />}
          {inputs.entry_type === 'side_steps' && <path d={`M ${lw.x1 - 10} ${lw.y1 + 28} L ${lw.x1} ${lw.y1 + 28} L ${lw.x1} ${lw.y1 + 72} L ${lw.x1 - 10} ${lw.y1 + 72} L ${lw.x1 - 10} ${lw.y1 + 63} L ${lw.x1 - 5} ${lw.y1 + 63} L ${lw.x1 - 5} ${lw.y1 + 37} L ${lw.x1 - 10} ${lw.y1 + 37} Z`} fill={C.steps} stroke={C.stepsStroke} strokeWidth="2" />}
          {inputs.entry_type === 'recessed_steps' && <rect x={stepsMidX - 30} y={se.y1 - 12} width="60" height="20" rx="10" fill={C.steps} stroke={C.stepsStroke} strokeWidth="1.8" />}

          {/* Placed features — Tanning Ledge */}
          {hasLedge && <rect x={ledgeX} y={lz.y + 2} width={ledgeW} height={ledgeH} rx="4" fill={C.ledge} stroke={C.ledgeStroke} strokeWidth="2" />}

          {/* Placed features — Bench */}
          {inputs.bench_seating === 'one_side' && sd.benchLeft && <rect x={sd.benchLeft.x} y={sd.benchLeft.y} width={sd.benchLeft.w} height={sd.benchLeft.h} rx="2" fill={C.bench} stroke={C.benchStroke} strokeWidth="1.8" />}
          {inputs.bench_seating === 'deep_end' && sd.benchDeep && <rect x={sd.benchDeep.x} y={sd.benchDeep.y} width={sd.benchDeep.w} height={sd.benchDeep.h} rx="2" fill={C.bench} stroke={C.benchStroke} strokeWidth="1.8" />}
          {inputs.bench_seating === 'wraparound' && <>
            {sd.benchLeft && <rect x={sd.benchLeft.x} y={sd.benchLeft.y} width={sd.benchLeft.w} height={sd.benchLeft.h} rx="2" fill={C.bench} stroke={C.benchStroke} strokeWidth="1.5" />}
            {sd.benchDeep && <rect x={sd.benchDeep.x} y={sd.benchDeep.y} width={sd.benchDeep.w} height={sd.benchDeep.h} rx="2" fill={C.bench} stroke={C.benchStroke} strokeWidth="1.5" />}
            {sd.benchRight && <rect x={sd.benchRight.x} y={sd.benchRight.y} width={sd.benchRight.w} height={sd.benchRight.h} rx="2" fill={C.bench} stroke={C.benchStroke} strokeWidth="1.5" />}
          </>}

          {/* Placed features — Spa */}
          {hasSpa && (() => {
            const spaR = inputs.spa_shape === 'round';
            const r = 23;
            const poolEdgeX = spaA.x < 100 ? lw.x1 : spaA.x > 200 ? rw.x1 : sd.poolCenter.x;
            return <>
              {inputs.spa_type === 'attached' && <line x1={spaA.x} y1={spaA.y} x2={poolEdgeX} y2={spaA.y} stroke={C.spa} strokeWidth="8" />}
              {spaR ? <circle cx={spaA.x} cy={spaA.y} r={r} fill={C.spa} stroke={C.spaStroke} strokeWidth="2.8" />
                : <rect x={spaA.x - r + 3} y={spaA.y - r + 5} width={r*2 - 6} height={r*2 - 10} rx={inputs.spa_shape === 'freeform' ? 14 : 5} fill={C.spa} stroke={C.spaStroke} strokeWidth="2.8" />}
              <text x={spaA.x} y={spaA.y + 4} textAnchor="middle" fill="white" style={{fontSize:'9px',fontWeight:800}}>SPA</text>
            </>;
          })()}

          {/* Static features — Slide */}
          {hasSlide && slPos && <>
            <rect x={slPos.px - 14} y={slPos.py - 10} width="28" height="20" rx="4" fill={C.slide} stroke={C.slideStroke} strokeWidth="1.8" />
            <text x={slPos.px} y={slPos.py + 4} textAnchor="middle" fill="white" style={{fontSize:'6px',fontWeight:700}}>SLIDE</text>
          </>}

          {/* Static — Water features */}
          {hasWater && ww && (() => {
            const wwLen = ww.x2 - ww.x1;
            const wwMid = (ww.x1 + ww.x2) / 2;
            if (inputs.water_feature_type === 'rock_waterfall') return <path d={`M ${wwMid - 18} ${ww.y1} C ${wwMid - 24} ${ww.y1 + 10}, ${wwMid - 18} ${ww.y1 + 20}, ${wwMid} ${ww.y1 + 22} C ${wwMid + 18} ${ww.y1 + 20}, ${wwMid + 24} ${ww.y1 + 10}, ${wwMid + 18} ${ww.y1}`} fill="#978B7B" stroke="#6B6158" strokeWidth="1.5" />;
            if (inputs.water_feature_type === 'sheer_descents') return <g>{[ww.x1 + wwLen*0.2, ww.x1 + wwLen*0.5, ww.x1 + wwLen*0.8].map((x,i) => <rect key={i} x={x-11} y={ww.y1-2} width="22" height="8" rx="2" fill={C.water} stroke={C.waterStroke} strokeWidth="1" />)}</g>;
            return null;
          })()}

          {/* Static — Fire features */}
          {(inputs.fire_feature_type === 'bowls' || inputs.fire_feature_type === 'both') && sd.fireBowls?.map((fb, i) =>
            <g key={`fb${i}`}><circle cx={fb.x} cy={fb.y} r="10" fill={C.fire} stroke="#993322" strokeWidth="1.3" /><circle cx={fb.x} cy={fb.y} r="4.5" fill={C.fireCore} /></g>
          )}
          {(inputs.fire_feature_type === 'fire_pit' || inputs.fire_feature_type === 'both') && sd.firePit &&
            <g><circle cx={sd.firePit.x} cy={sd.firePit.y} r="14" fill={C.fire} stroke="#993322" strokeWidth="1.4" /><circle cx={sd.firePit.x} cy={sd.firePit.y} r="6" fill={C.fireCore} /></g>
          }

          {/* Placement zones — highlighted during placement mode */}
          {zones.map(zone => (
            <rect key={zone.id} x={zone.rect.x} y={zone.rect.y} width={zone.rect.w} height={zone.rect.h}
              rx="4" fill={C.zoneFill} stroke={C.zoneHighlight} strokeWidth="2" strokeDasharray="4 2"
              className="cursor-pointer animate-pulse" style={{animationDuration:'1.5s'}}
              onClick={() => handleZoneClick(zone)} />
          ))}
          {zones.map(zone => (
            <text key={`lbl-${zone.id}`} x={zone.rect.x + zone.rect.w / 2} y={zone.rect.y + zone.rect.h / 2 + 3}
              textAnchor="middle" fill={C.zoneHighlight} style={{fontSize:'7px',fontWeight:700}} pointerEvents="none">
              {zone.label}
            </text>
          ))}
        </svg>
      </div>

    </div>
  );
}

function FeatureButton({ label, active, placing, onAdd, onRemove, color }) {
  if (placing) {
    return (
      <button onClick={() => {}} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 border-[#F59E0B] bg-amber-50 text-[#92400E] text-xs font-semibold animate-pulse">
        <div className="w-3 h-3 rounded-sm" style={{background: '#F59E0B'}} /> Placing {label}...
      </button>
    );
  }
  if (active) {
    return (
      <button onClick={onRemove} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-xs font-medium text-slate-700 hover:border-red-300 hover:text-red-600 transition-colors group">
        <div className="w-3 h-3 rounded-sm" style={{background: color}} />
        {label}
        <span className="text-slate-300 group-hover:text-red-400 ml-0.5">×</span>
      </button>
    );
  }
  return (
    <button onClick={onAdd} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 border-dashed border-slate-300 bg-white text-xs font-medium text-slate-500 hover:border-[#F59E0B] hover:text-[#92400E] transition-colors">
      <span className="text-base leading-none">+</span> {label}
    </button>
  );
}

function PoolDesignEditor({ inputs, onUpdate }) {
  const shape = inputs.pool_shape_template;
  const zones = shape ? getZonesForShape(shape) : [];
  const [placingFeature, setPlacingFeature] = useState(null);
  const [draggingFeature, setDraggingFeature] = useState(null);
  const [hoveredZone, setHoveredZone] = useState(null);
  const [featurePositions, setFeaturePositions] = useState({});
  const [spaType, setSpaType] = useState('attached'); // 'attached' | 'integrated' | 'detached'
  const canvasRef = useRef(null);

  // Parametric layout — computed from pool dimensions, passed to PoolLayoutEditor
  const layout = computeLayout(inputs.length, inputs.width);

  // Clear featurePositions when pool shape changes
  useEffect(() => {
    setFeaturePositions({});
  }, [shape]);

  // Escape key cancels placement or drag mode
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') { setPlacingFeature(null); setDraggingFeature(null); setHoveredZone(null); } };
    if (placingFeature || draggingFeature) { window.addEventListener('keydown', handler); return () => window.removeEventListener('keydown', handler); }
  }, [placingFeature, draggingFeature]);

  // Get SVG center coordinates for a snap zone — mapped to parametric viewBox
  const getZoneSVGCenter = (zone) => {
    const { ox, oy, poolW, poolH } = layout;
    const deckM = 24; // deck margin for exterior zones
    // Use semantic zone positions based on the parametric pool body
    const map = {
      'shallow-end':        { x: ox + poolW * 0.5,  y: oy + poolH * 0.1 },
      'deep-end':           { x: ox + poolW * 0.5,  y: oy + poolH * 0.9 },
      'left-wall':          { x: ox + poolW * 0.1,  y: oy + poolH * 0.5 },
      'right-wall':         { x: ox + poolW * 0.9,  y: oy + poolH * 0.5 },
      'top-left-corner':    { x: ox + poolW * 0.15, y: oy + poolH * 0.15 },
      'top-right-corner':   { x: ox + poolW + deckM, y: oy + deckM },
      'top-right':          { x: ox + poolW + deckM, y: oy + deckM },
      'top-center':         { x: ox + poolW * 0.5,  y: oy - deckM },
      'bottom-left-corner': { x: ox - deckM,         y: oy + poolH + deckM },
      'bottom-right-corner':{ x: ox + poolW + deckM, y: oy + poolH + deckM },
      'inner-corner':       { x: ox + poolW * 0.5,  y: oy + poolH * 0.45 },
      'vanishing-edge':     { x: ox + poolW * 0.5,  y: oy + poolH * 0.85 },
    };
    return map[zone.id] || { x: ox + poolW * 0.5, y: oy + poolH * 0.5 };
  };

  // Convert client coordinates to viewBox percentages using the canvas container
  const clientToPercent = (clientX, clientY) => {
    const el = canvasRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    return { x: (clientX - rect.left) / rect.width, y: (clientY - rect.top) / rect.height };
  };

  // Snap check: if within 24px of a valid zone center (in SVG coords), return that zone
  const findSnapZone = (pctX, pctY, validZoneList) => {
    const svgX = pctX * layout.viewW;
    const svgY = pctY * layout.viewH;
    const snapDist = 24;
    for (const z of validZoneList) {
      const center = getZoneSVGCenter(z);
      const dx = svgX - center.x;
      const dy = svgY - center.y;
      if (Math.sqrt(dx * dx + dy * dy) < snapDist) return z;
    }
    return null;
  };

  // Apply semantic u() updates for a feature placement
  const applySemanticUpdate = (featureId, zoneId) => {
    const value = getPlacementValue(featureId, zoneId);
    if (featureId === 'steps') onUpdate('entry_type', value);
    else if (featureId === 'tanning-ledge') { onUpdate('tanning_ledge_size', value); onUpdate('tanning_ledge', true); }
    else if (featureId === 'bench') onUpdate('bench_seating', value);
    else if (featureId === 'spa') { onUpdate('spa_location', value); onUpdate('spa_type', 'attached'); onUpdate('spa', true); }
    else if (featureId === 'water-feature') { onUpdate('water_feature_type', value); onUpdate('waterfall', true); }
    else if (featureId === 'fire-feature') { onUpdate('fire_feature_type', value); onUpdate('fire_features', true); }
    else if (featureId === 'slide') { onUpdate('slide_placement', value); onUpdate('slide', true); }
  };

  // Feature definitions with field mappings
  const featureStatus = [
    { id: 'steps', label: 'Steps', placed: !!(inputs.entry_type && inputs.entry_type !== 'no_steps'), field: 'entry_type', clearValue: 'no_steps' },
    { id: 'tanning-ledge', label: 'Tanning Ledge', placed: !!(inputs.tanning_ledge_size && inputs.tanning_ledge_size !== 'none'), field: 'tanning_ledge_size', clearValue: 'none' },
    { id: 'bench', label: 'Bench', placed: !!(inputs.bench_seating && inputs.bench_seating !== 'none'), field: 'bench_seating', clearValue: 'none' },
    { id: 'spa', label: 'Spa', placed: !!(inputs.spa_type && inputs.spa_type !== 'none'), field: 'spa_location', clearValue: null },
    { id: 'water-feature', label: 'Water Feature', placed: !!(inputs.water_feature_type && inputs.water_feature_type !== 'none'), field: 'water_feature_type', clearValue: 'none' },
    { id: 'fire-feature', label: 'Fire Feature', placed: !!(inputs.fire_feature_type && inputs.fire_feature_type !== 'none'), field: 'fire_feature_type', clearValue: 'none' },
    { id: 'slide', label: 'Slide', placed: !!(inputs.slide_placement && inputs.slide_placement !== 'none'), field: 'slide_placement', clearValue: 'none' },
  ];

  // Zone-to-field value mapping
  const getPlacementValue = (featureId, zoneId) => {
    if (featureId === 'steps') {
      const map = { 'shallow-end': 'full_width_steps', 'top-left-corner': 'corner_steps', 'left-wall': 'side_steps', 'top-center': 'recessed_steps' };
      return map[zoneId] || 'full_width_steps';
    }
    if (featureId === 'tanning-ledge') return 'medium';
    if (featureId === 'bench') {
      const map = { 'shallow-end': 'one_side', 'left-wall': 'one_side', 'right-wall': 'one_side', 'deep-end': 'deep_end' };
      return map[zoneId] || 'one_side';
    }
    if (featureId === 'spa') {
      const map = { 'left-wall': 'left', 'right-wall': 'right', 'top-center': 'center', 'top-left-corner': 'left', 'top-right-corner': 'right', 'right-bulge': 'right' };
      return map[zoneId] || 'right';
    }
    if (featureId === 'water-feature') return 'sheer_descents';
    if (featureId === 'fire-feature') return 'bowls';
    if (featureId === 'slide') {
      const map = { 'left-wall': 'left', 'right-wall': 'right', 'deep-end': 'deep_end' };
      return map[zoneId] || 'deep_end';
    }
    return '';
  };

  const handleChipClick = (feature) => {
    if (feature.placed) {
      // Remove the feature + clear its position
      if (feature.id === 'spa') { onUpdate('spa_type', 'none'); onUpdate('spa', false); setSpaType('attached'); }
      else if (feature.id === 'steps') onUpdate('entry_type', 'no_steps');
      else if (feature.id === 'tanning-ledge') { onUpdate('tanning_ledge_size', 'none'); onUpdate('tanning_ledge', false); }
      else if (feature.id === 'bench') onUpdate('bench_seating', 'none');
      else if (feature.id === 'water-feature') { onUpdate('water_feature_type', 'none'); onUpdate('waterfall', false); }
      else if (feature.id === 'fire-feature') { onUpdate('fire_feature_type', 'none'); onUpdate('fire_features', false); }
      else if (feature.id === 'slide') { onUpdate('slide_placement', 'none'); onUpdate('slide', false); }
      setFeaturePositions(prev => { const next = { ...prev }; delete next[feature.id]; return next; });
      setPlacingFeature(null);
    } else {
      setPlacingFeature(placingFeature === feature.id ? null : feature.id);
    }
  };

  // Full-canvas click handler — places feature at clicked coordinate with snap check
  const handleCanvasClick = (e) => {
    if (!placingFeature) return;
    const pct = clientToPercent(e.clientX, e.clientY);
    if (!pct) return;
    const currentValidZones = zones.filter(z => z.allowedFeatures.includes(placingFeature));
    const snappedZone = findSnapZone(pct.x, pct.y, currentValidZones);

    if (snappedZone) {
      // Snap to zone center
      const center = getZoneSVGCenter(snappedZone);
      setFeaturePositions(prev => ({ ...prev, [placingFeature]: { x: center.x / layout.viewW, y: center.y / layout.viewH } }));
      applySemanticUpdate(placingFeature, snappedZone.id);
      toast.success(`${featureStatus.find(f => f.id === placingFeature)?.label} placed at ${snappedZone.label}`);
    } else {
      // Free placement — use nearest zone's semantic value as default
      const fallbackZone = currentValidZones[0];
      setFeaturePositions(prev => ({ ...prev, [placingFeature]: { x: pct.x, y: pct.y } }));
      if (fallbackZone) applySemanticUpdate(placingFeature, fallbackZone.id);
      else applySemanticUpdate(placingFeature, 'shallow-end');
      toast.success(`${featureStatus.find(f => f.id === placingFeature)?.label} placed at custom position`);
    }
    setPlacingFeature(null);
  };

  const handleZoneClick = (zone) => {
    if (!placingFeature) return;
    // Snap to this zone
    const center = getZoneSVGCenter(zone);
    setFeaturePositions(prev => ({ ...prev, [placingFeature]: { x: center.x / layout.viewW, y: center.y / layout.viewH } }));
    applySemanticUpdate(placingFeature, zone.id);
    toast.success(`${featureStatus.find(f => f.id === placingFeature)?.label} placed at ${zone.label}`);
    setPlacingFeature(null);
  };

  // --- Phase 3: Drag to Reposition ---
  const handleDragStart = (e, feature) => {
    e.dataTransfer.setData('text/plain', feature.id);
    e.dataTransfer.effectAllowed = 'move';
    setDraggingFeature(feature.id);
    setPlacingFeature(null); // cancel any active click-to-place
  };

  const handleDragEnd = () => {
    setDraggingFeature(null);
    setHoveredZone(null);
  };

  const handleZoneDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleZoneDragEnter = (zoneId) => {
    setHoveredZone(zoneId);
  };

  const handleZoneDragLeave = (e, zoneId) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setHoveredZone(prev => prev === zoneId ? null : prev);
    }
  };

  const handleZoneDrop = (e, zone) => {
    e.preventDefault();
    e.stopPropagation();
    const featureId = e.dataTransfer.getData('text/plain') || draggingFeature;
    if (!featureId) return;
    const center = getZoneSVGCenter(zone);
    setFeaturePositions(prev => ({ ...prev, [featureId]: { x: center.x / layout.viewW, y: center.y / layout.viewH } }));
    applySemanticUpdate(featureId, zone.id);
    toast.success(`${featureStatus.find(f => f.id === featureId)?.label} moved to ${zone.label}`);
    setDraggingFeature(null);
    setHoveredZone(null);
  };

  // Full-canvas drop handler — places feature at drop coordinate with snap check
  const handleCanvasDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleCanvasDrop = (e) => {
    e.preventDefault();
    const featureId = e.dataTransfer.getData('text/plain') || draggingFeature;
    if (!featureId) return;
    const pct = clientToPercent(e.clientX, e.clientY);
    if (!pct) return;
    const currentValidZones = zones.filter(z => z.allowedFeatures.includes(featureId));
    const snappedZone = findSnapZone(pct.x, pct.y, currentValidZones);

    if (snappedZone) {
      const center = getZoneSVGCenter(snappedZone);
      setFeaturePositions(prev => ({ ...prev, [featureId]: { x: center.x / layout.viewW, y: center.y / layout.viewH } }));
      applySemanticUpdate(featureId, snappedZone.id);
      toast.success(`${featureStatus.find(f => f.id === featureId)?.label} moved to ${snappedZone.label}`);
    } else {
      setFeaturePositions(prev => ({ ...prev, [featureId]: { x: pct.x, y: pct.y } }));
      toast.success(`${featureStatus.find(f => f.id === featureId)?.label} moved to custom position`);
    }
    setDraggingFeature(null);
    setHoveredZone(null);
  };

  // Click-based drop (for canvas indicator → click destination flow)
  const handleCanvasClickDrop = (e) => {
    if (!draggingFeature) return;
    const pct = clientToPercent(e.clientX, e.clientY);
    if (!pct) return;
    const currentValidZones = zones.filter(z => z.allowedFeatures.includes(draggingFeature));
    const snappedZone = findSnapZone(pct.x, pct.y, currentValidZones);

    if (snappedZone) {
      const center = getZoneSVGCenter(snappedZone);
      setFeaturePositions(prev => ({ ...prev, [draggingFeature]: { x: center.x / layout.viewW, y: center.y / layout.viewH } }));
      applySemanticUpdate(draggingFeature, snappedZone.id);
      toast.success(`${featureStatus.find(f => f.id === draggingFeature)?.label} moved to ${snappedZone.label}`);
    } else {
      setFeaturePositions(prev => ({ ...prev, [draggingFeature]: { x: pct.x, y: pct.y } }));
      toast.success(`${featureStatus.find(f => f.id === draggingFeature)?.label} moved to custom position`);
    }
    setDraggingFeature(null);
    setHoveredZone(null);
  };

  // Active feature for zone filtering (either click-to-place or drag)
  const activeFeature = placingFeature || draggingFeature;

  // Valid zones for the currently active feature — spa type aware
  const validZones = (() => {
    if (!activeFeature) return [];
    let filtered = zones.filter(z => z.allowedFeatures.includes(activeFeature));
    // Spa type zone preference
    if (activeFeature === 'spa') {
      if (spaType === 'integrated') {
        filtered = filtered.filter(z => z.placement === 'interior');
      } else {
        filtered = filtered.filter(z => z.placement === 'exterior');
      }
      // If no zones match preference, fall back to all spa zones
      if (filtered.length === 0) filtered = zones.filter(z => z.allowedFeatures.includes('spa'));
    }
    return filtered;
  })();

  // --- Phase 5: Scaled Feature Indicators on Canvas ---
  const FEATURE_INDICATORS = {
    'steps':          { icon: '\u{1FA9C}', color: '#6B7280', fill: '#B8BFC8', stroke: '#6B7A8A', label: 'Steps' },
    'tanning-ledge':  { icon: '\u2600\uFE0F', color: '#0D9488', fill: '#7DC8BA', stroke: '#4A9E8F', label: 'Ledge' },
    'bench':          { icon: '\u{1FA91}', color: '#92400E', fill: '#AEA599', stroke: '#8C8278', label: 'Bench' },
    'spa':            { icon: '\u{1F30A}', color: '#1E40AF', fill: '#5AADCF', stroke: '#2A7A9E', label: 'Spa' },
    'water-feature':  { icon: '\u{1F4A7}', color: '#2563EB', fill: '#4A8FD4', stroke: '#2E6BA8', label: 'Water' },
    'fire-feature':   { icon: '\u{1F525}', color: '#D97706', fill: '#C44B30', stroke: '#993322', label: 'Fire' },
    'slide':          { icon: '\u{1F3BF}', color: '#059669', fill: '#A3D9A5', stroke: '#4A9E5F', label: 'Slide' },
  };

  // Compute scaled SVG geometry for each placed feature
  // If featurePositions has a custom position, center the feature shape there
  const getFeatureGeometry = (featureId) => {
    const { viewW, viewH, ox, oy, poolW, poolH, scale } = layout;
    const s = scale; // px per foot
    const rx = ox + poolW;
    const by = oy + poolH;
    const deckM = 18;

    // Check for custom position override
    const customPos = featurePositions[featureId];
    if (customPos) {
      // Render a generic indicator at the custom position
      const cx = customPos.x * viewW;
      const cy = customPos.y * viewH;
      // Each feature gets its scaled size centered at (cx, cy)
      if (featureId === 'steps') {
        const w = 4*s, h = 4*s;
        return { type: 'rect', x: cx - w/2, y: cy - h/2, w, h, rx: 3 };
      }
      if (featureId === 'tanning-ledge') {
        const w = 8*s, h = 1.5*s;
        return { type: 'rect', x: cx - w/2, y: cy - h/2, w, h, rx: 4 };
      }
      if (featureId === 'bench') {
        const w = 1.5*s, h = poolH*0.5;
        return { type: 'rect', x: cx - w/2, y: cy - h/2, w, h };
      }
      if (featureId === 'spa') {
        const minR = Math.max(4*s, 40); // minimum 80px diameter (40 radius) in SVG units
        if (spaType === 'integrated') {
          const w = Math.max(8*s, 80), h = Math.max(8*s, 80);
          const pad = 4;
          // Snap to nearest corner/wall edge with 4px padding
          let ix, iy;
          // Determine horizontal: left half → flush left, right half → flush right, middle → center
          if (cx < ox + poolW * 0.35) ix = ox + pad;
          else if (cx > ox + poolW * 0.65) ix = ox + poolW - w - pad;
          else ix = ox + (poolW - w) / 2;
          // Determine vertical: top third → flush top, bottom third → flush bottom, middle → center
          if (cy < oy + poolH * 0.35) iy = oy + pad;
          else if (cy > oy + poolH * 0.65) iy = oy + poolH - h - pad;
          else iy = oy + (poolH - h) / 2;
          return { type: 'rect', x: ix, y: iy, w, h, rx: 12, spaVariant: 'integrated' };
        }
        if (spaType === 'detached') {
          return { type: 'circle', cx, cy, r: minR, spaVariant: 'detached' };
        }
        return { type: 'circle', cx, cy, r: minR, spaVariant: 'attached' };
      }
      if (featureId === 'water-feature') {
        const w = 2*s, h = 2*s;
        return { type: 'rect', x: cx - w/2, y: cy - h/2, w, h, rx: 3 };
      }
      if (featureId === 'fire-feature') {
        return { type: 'circle', cx, cy, r: 1*s };
      }
      if (featureId === 'slide') {
        const w = 3*s, h = 6*s;
        return { type: 'rect', x: cx - w/2, y: cy - h/2, w, h, rx: 4 };
      }
    }

    // Default: derive position from semantic placement value
    if (featureId === 'steps') {
      const et = inputs.entry_type;
      if (et === 'corner_steps') return { type: 'polygon', points: `${ox},${oy} ${ox + 4*s},${oy} ${ox},${oy + 4*s}` };
      if (et === 'side_steps') return { type: 'rect', x: ox - 1.5*s, y: oy + poolH*0.2, w: 1.5*s, h: 3*s };
      if (et === 'recessed_steps') return { type: 'rect', x: ox + poolW*0.5 - 2*s, y: oy - 1*s, w: 4*s, h: 1.5*s, rx: s*0.5 };
      return { type: 'rect', x: ox + 2, y: oy, w: poolW - 4, h: 1.5*s }; // full_width
    }
    if (featureId === 'tanning-ledge') return { type: 'rect', x: ox + poolW*0.5 - 4*s, y: oy + 2, w: 8*s, h: 1.5*s, rx: 4 };
    if (featureId === 'bench') {
      const bs = inputs.bench_seating;
      if (bs === 'deep_end') return { type: 'rect', x: ox + poolW*0.08, y: by - 1.5*s, w: poolW*0.84, h: 1.5*s };
      if (bs === 'wraparound') return { type: 'path', d: `M ${ox} ${oy + poolH*0.12} L ${ox + 1.5*s} ${oy + poolH*0.12} L ${ox + 1.5*s} ${by - 1.5*s} L ${rx - 1.5*s} ${by - 1.5*s} L ${rx - 1.5*s} ${oy + poolH*0.12} L ${rx} ${oy + poolH*0.12} L ${rx} ${by} L ${ox} ${by} Z` };
      return { type: 'rect', x: ox, y: oy + poolH*0.12, w: 1.5*s, h: poolH*0.76 }; // one_side (left)
    }
    if (featureId === 'spa') {
      const loc = inputs.spa_location;
      const minR = Math.max(4*s, 40);
      let cx, cy;
      if (loc === 'left') { cx = ox - deckM - minR*0.5; cy = oy + poolH*0.5; }
      else if (loc === 'center') { cx = ox + poolW*0.5; cy = oy - deckM - minR*0.5; }
      else { cx = rx + deckM + minR*0.5; cy = oy + poolH*0.5; }
      if (spaType === 'integrated') {
        const w = Math.max(8*s, 80), h = Math.max(8*s, 80);
        const pad = 4;
        // Default integrated position: top-left corner flush
        return { type: 'rect', x: ox + pad, y: oy + pad, w, h, rx: 12, spaVariant: 'integrated' };
      }
      if (spaType === 'detached') {
        return { type: 'circle', cx, cy, r: minR, spaVariant: 'detached' };
      }
      return { type: 'circle', cx, cy, r: minR, spaVariant: 'attached' };
    }
    if (featureId === 'water-feature') return { type: 'rect', x: ox + poolW*0.5 - 1*s, y: by - 2*s, w: 2*s, h: 2*s, rx: 3 };
    if (featureId === 'fire-feature') return { type: 'circle', cx: ox - deckM, cy: by + 10, r: 1*s };
    if (featureId === 'slide') {
      const sp = inputs.slide_placement;
      if (sp === 'left') return { type: 'rect', x: ox - deckM - 3*s, y: oy + poolH*0.4, w: 3*s, h: 6*s, rx: 4 };
      if (sp === 'right') return { type: 'rect', x: rx + deckM, y: oy + poolH*0.4, w: 3*s, h: 6*s, rx: 4 };
      return { type: 'rect', x: ox + poolW*0.5 - 1.5*s, y: by + 4, w: 3*s, h: 6*s, rx: 4 }; // deep_end
    }
    return null;
  };

  // Build SVG elements for all placed features
  const getFeatureSVGElements = () => {
    return featureStatus.filter(f => f.placed).map(f => {
      const geo = getFeatureGeometry(f.id);
      const ind = FEATURE_INDICATORS[f.id];
      if (!geo) return null;
      const labelX = geo.type === 'circle' ? geo.cx : (geo.x + (geo.w || 0) / 2);
      const labelY = geo.type === 'circle' ? geo.cy : (geo.y + (geo.h || 0) / 2);
      // Spa variant styling overrides
      let fill = ind.fill, stroke = ind.stroke, opacity = 0.85, strokeDash = undefined, label = ind.label, strokeW = 1.5;
      if (f.id === 'spa' && geo.spaVariant) {
        if (geo.spaVariant === 'integrated') { fill = '#0D9488'; stroke = '#065F5B'; opacity = 0.7; strokeW = 2; label = 'INT. SPA'; }
        else if (geo.spaVariant === 'detached') { fill = '#6B7280'; stroke = '#374151'; strokeDash = '8 4'; strokeW = 3; label = 'DET. SPA'; }
        else { fill = '#1E40AF'; stroke = '#1E3A8A'; strokeW = 3; label = 'SPA'; }
      }
      return { id: f.id, geo, fill, stroke, opacity, strokeDash, strokeW, icon: ind.icon, label, labelX, labelY };
    }).filter(Boolean);
  };

  // Click handler for canvas feature indicators — activates drag/reposition mode
  const handleIndicatorClick = (featureId) => {
    setDraggingFeature(featureId);
    setPlacingFeature(null);
  };

  // Zone position mapping — parametric from computeLayout()
  // Pool body as % of viewBox, with 8px interior padding
  const getZoneStyle = (zone, featureContext) => {
    const { viewW, viewH, ox, oy, poolW, poolH } = layout;
    const zoneId = zone.id;
    const forceExterior = (placingFeature === 'spa' && spaType !== 'integrated') || placingFeature === 'slide' || (featureContext === 'spa' && spaType !== 'integrated') || featureContext === 'slide';
    const isInterior = !forceExterior && zone.placement === 'interior';
    const pad = 8; // px interior padding

    if (isInterior) {
      const pctL = v => ((v / viewW) * 100).toFixed(1) + '%';
      const pctT = v => ((v / viewH) * 100).toFixed(1) + '%';
      const interiorMap = {
        'shallow-end':    { top: pctT(oy+pad),                left: pctL(ox+poolW*0.2),  width: pctL(poolW*0.6),  height: pctT(poolH*0.18) },
        'deep-end':       { top: pctT(oy+poolH-pad-poolH*0.18), left: pctL(ox+poolW*0.2),  width: pctL(poolW*0.6),  height: pctT(poolH*0.18) },
        'left-wall':      { top: pctT(oy+poolH*0.15),         left: pctL(ox+pad),         width: pctL(poolW*0.18), height: pctT(poolH*0.55) },
        'right-wall':     { top: pctT(oy+poolH*0.15),         left: pctL(ox+poolW-pad-poolW*0.18), width: pctL(poolW*0.18), height: pctT(poolH*0.55) },
        'top-left-corner':{ top: pctT(oy+pad),                left: pctL(ox+pad),         width: pctL(poolW*0.25), height: pctT(poolH*0.25) },
        'inner-corner':   { top: pctT(oy+poolH*0.3),          left: pctL(ox+poolW*0.3),   width: pctL(poolW*0.35), height: pctT(poolH*0.3) },
        'vanishing-edge': { top: pctT(oy+poolH*0.7),          left: pctL(ox+poolW*0.1),   width: pctL(poolW*0.8),  height: pctT(poolH*0.2) },
      };
      return interiorMap[zoneId] || { top: pctT(oy+poolH*0.2), left: pctL(ox+poolW*0.2), width: pctL(poolW*0.6), height: pctT(poolH*0.4) };
    } else {
      const pctL = v => ((v / viewW) * 100).toFixed(1) + '%';
      const pctT = v => ((v / viewH) * 100).toFixed(1) + '%';
      const dm = 18; // deck margin
      const exteriorMap = {
        'left-wall':           { top: pctT(oy+poolH*0.2),  left: pctL(2),              width: pctL(ox-4),       height: pctT(poolH*0.55) },
        'right-wall':          { top: pctT(oy+poolH*0.2),  left: pctL(ox+poolW+2),     width: pctL(viewW-ox-poolW-4), height: pctT(poolH*0.55) },
        'top-center':          { top: pctT(2),              left: pctL(ox+poolW*0.2),   width: pctL(poolW*0.6),  height: pctT(oy-4) },
        'top-left-corner':     { top: pctT(2),              left: pctL(2),              width: pctL(ox+poolW*0.15), height: pctT(oy+poolH*0.1) },
        'top-right-corner':    { top: pctT(2),              left: pctL(ox+poolW*0.75),  width: pctL(viewW-ox-poolW*0.75-2), height: pctT(oy+poolH*0.1) },
        'top-right':           { top: pctT(2),              left: pctL(ox+poolW*0.75),  width: pctL(viewW-ox-poolW*0.75-2), height: pctT(oy+poolH*0.1) },
        'bottom-left-corner':  { top: pctT(oy+poolH+2),    left: pctL(2),              width: pctL(ox+poolW*0.15), height: pctT(viewH-oy-poolH-4) },
        'bottom-right-corner': { top: pctT(oy+poolH+2),    left: pctL(ox+poolW*0.75),  width: pctL(viewW-ox-poolW*0.75-2), height: pctT(viewH-oy-poolH-4) },
        'deep-end':            { top: pctT(oy+poolH+2),    left: pctL(ox+poolW*0.15),  width: pctL(poolW*0.7),  height: pctT(viewH-oy-poolH-4) },
      };
      return exteriorMap[zoneId] || { top: pctT(2), left: pctL(2), width: pctL(ox-4), height: pctT(oy-4) };
    }
  };

  // Parametric SVG helper values
  const layoutMaxWidth = Math.min(960, Math.round(layout.viewW / layout.viewH * 500));
  const layoutAspect = `${layout.viewW}/${layout.viewH}`;
  const layoutViewBox = `0 0 ${layout.viewW} ${layout.viewH}`;
  const poolPath = generatePoolPath(shape, layout.poolW, layout.poolH, layout.ox, layout.oy);

  const shapeLabels = { rectangle:'Rectangle', auto_cover_rectangle:'Auto-Cover Rectangle', roman:'Roman', freeform:'Freeform', grecian:'Grecian', l_shape:'L-Shape', lazy_l:'Lazy-L', kidney:'Kidney', geometric_custom:'Geometric Custom', compact_plunge:'Compact Plunge', negative_edge:'Negative Edge' };
  const depthLabels = { flat:'Flat Bottom', sport_bottom:'Sport Bottom', deep_end_hopper:'Deep End Hopper' };

  if (!shape) {
    return (
      <div className="border-2 border-dashed border-slate-200 rounded-xl text-center bg-slate-50/50 p-8">
        <p className="text-sm text-slate-400 font-medium">Select a pool shape above to open the design editor</p>
      </div>
    );
  }

  return (
    <div className="border border-slate-200 rounded-xl bg-white overflow-hidden shadow-sm" data-testid="pool-design-editor">
      <div className="flex">
        {/* Left Sidebar */}
        <div className="w-[280px] flex-shrink-0 border-r border-slate-100 bg-slate-50 p-4 flex flex-col gap-4">
          {/* Features section */}
          <div>
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Features</p>
            <div className="flex flex-wrap gap-1.5">
              {featureStatus.map(f => (
                <button key={f.id} onClick={() => handleChipClick(f)}
                  draggable={f.placed && placingFeature !== f.id ? "true" : undefined}
                  onDragStart={f.placed ? (e) => handleDragStart(e, f) : undefined}
                  onDragEnd={f.placed ? handleDragEnd : undefined}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer ${
                    placingFeature === f.id || draggingFeature === f.id
                      ? 'bg-[#F59E0B] text-[#0F1D32] ring-2 ring-[#F59E0B] ring-offset-1 shadow-sm'
                      : f.placed
                        ? 'bg-[#0F1D32] text-white hover:bg-[#1a2d45]'
                        : 'bg-white border border-slate-200 text-slate-500 hover:border-[#F59E0B] hover:text-[#92400E]'
                  }`}
                  data-testid={`feature-chip-${f.id}`}>
                  {f.placed && placingFeature !== f.id && <span className="text-[10px] opacity-60 cursor-grab" style={{pointerEvents:'none'}} title="Drag to reposition">&#x2807;</span>}
                  <div className={`w-2 h-2 rounded-full ${placingFeature === f.id ? 'bg-white' : f.placed ? 'bg-[#F59E0B]' : 'bg-slate-300'}`} />
                  {f.label}
                  {f.placed && placingFeature !== f.id && <span className="ml-0.5 text-[10px] opacity-60">×</span>}
                </button>
              ))}
            </div>
          </div>

          {/* Pool Info section */}
          <div>
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Pool Info</p>
            <div className="space-y-1.5 text-xs text-slate-600">
              <div className="flex justify-between"><span className="text-slate-400">Shape</span><span className="font-medium">{shapeLabels[shape] || shape}</span></div>
              {inputs.length && inputs.width && <div className="flex justify-between"><span className="text-slate-400">Size</span><span className="font-medium">{inputs.length}' × {inputs.width}'</span></div>}
              {inputs.depth_profile_type && <div className="flex justify-between"><span className="text-slate-400">Depth</span><span className="font-medium">{depthLabels[inputs.depth_profile_type] || inputs.depth_profile_type}</span></div>}
            </div>
          </div>

          {/* Zones info */}
          <div>
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Available Zones</p>
            <div className="space-y-1">
              {zones.map(z => {
                const isValid = validZones.some(vz => vz.id === z.id);
                return (
                  <div key={z.id} className={`text-[10px] flex items-center gap-1.5 ${isValid ? 'text-[#92400E] font-semibold' : 'text-slate-400'}`}>
                    <div className={`w-1.5 h-1.5 rounded-full ${isValid ? 'bg-[#F59E0B]' : 'bg-slate-300'}`} />
                    {z.label}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right Canvas */}
        <div className="flex-1 min-w-0">
          {/* Instruction bar — click-to-place mode */}
          {placingFeature && (
            <div className="px-4 py-2 bg-amber-50 border-b border-amber-200">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-[#92400E]">Click anywhere to place <span className="font-bold">{featureStatus.find(f => f.id === placingFeature)?.label}</span></p>
                <button onClick={() => setPlacingFeature(null)} className="text-[10px] text-slate-500 hover:text-red-600 border border-slate-300 rounded px-2 py-0.5 hover:border-red-300">Cancel</button>
              </div>
              {/* Spa type selector */}
              {placingFeature === 'spa' && (
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="text-[10px] text-slate-500 font-medium">Type:</span>
                  {[{v:'attached', l:'Attached'}, {v:'integrated', l:'Integrated'}, {v:'detached', l:'Detached'}].map(t => (
                    <button key={t.v} onClick={() => setSpaType(t.v)}
                      className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-all ${spaType === t.v ? 'bg-[#F59E0B] text-[#0F1D32]' : 'bg-white border border-slate-200 text-slate-500 hover:border-[#F59E0B]'}`}
                      data-testid={`spa-type-${t.v}`}>{t.l}</button>
                  ))}
                </div>
              )}
            </div>
          )}
          {/* Instruction bar — drag mode */}
          {draggingFeature && !placingFeature && (
            <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 flex items-center justify-between">
              <p className="text-xs font-semibold text-[#92400E]">Drop <span className="font-bold">{featureStatus.find(f => f.id === draggingFeature)?.label}</span> onto a highlighted zone to move it</p>
              <button onClick={() => { setDraggingFeature(null); setHoveredZone(null); }} className="text-[10px] text-slate-500 hover:text-red-600 border border-slate-300 rounded px-2 py-0.5 hover:border-red-300">Cancel</button>
            </div>
          )}

          <div ref={canvasRef} className="bg-[#F5F0E8] relative" style={{height: (placingFeature || draggingFeature) ? '470px' : '500px'}}>
            {/* Scaled grid overlay — 1ft minor lines, 5ft major lines with labels */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <svg viewBox={layoutViewBox} className="w-full h-full" style={{maxWidth:`${layoutMaxWidth}px`, display:'block', margin:'0 auto'}} aria-hidden="true">
                {(() => {
                  const { viewW, viewH, ox, oy, poolW, poolH, scale, lengthFt, widthFt } = layout;
                  const lines = [];
                  // Minor grid lines every 1ft (scale px)
                  for (let ft = 0; ft <= lengthFt; ft++) {
                    const x = ox + ft * scale;
                    lines.push(<line key={`vmin${ft}`} x1={x} y1={0} x2={x} y2={viewH} stroke="#9CA3AF" strokeWidth="0.3" opacity="0.15" />);
                  }
                  for (let ft = 0; ft <= widthFt; ft++) {
                    const y = oy + ft * scale;
                    lines.push(<line key={`hmin${ft}`} x1={0} y1={y} x2={viewW} y2={y} stroke="#9CA3AF" strokeWidth="0.3" opacity="0.15" />);
                  }
                  // Major grid lines every 5ft + labels
                  for (let ft = 5; ft <= lengthFt; ft += 5) {
                    const x = ox + ft * scale;
                    lines.push(<line key={`vmaj${ft}`} x1={x} y1={0} x2={x} y2={viewH} stroke="#9CA3AF" strokeWidth="0.7" opacity="0.3" />);
                    lines.push(<text key={`vlbl${ft}`} x={x} y={oy - 6} textAnchor="middle" fill="#94A3B8" fontSize="10" fontFamily="Manrope, sans-serif">{ft}&apos;</text>);
                  }
                  for (let ft = 5; ft <= widthFt; ft += 5) {
                    const y = oy + ft * scale;
                    lines.push(<line key={`hmaj${ft}`} x1={0} y1={y} x2={viewW} y2={y} stroke="#9CA3AF" strokeWidth="0.7" opacity="0.3" />);
                    lines.push(<text key={`hlbl${ft}`} x={ox - 6} y={y + 3} textAnchor="end" fill="#94A3B8" fontSize="10" fontFamily="Manrope, sans-serif">{ft}&apos;</text>);
                  }
                  return lines;
                })()}
              </svg>
            </div>

            {/* Pool Layout Editor (existing working component — untouched) */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <PoolLayoutEditor inputs={inputs} onUpdate={onUpdate} layout={layout} />
            </div>

            {/* Phase 6: Depth gradient overlay + Compass + Dimension labels — parametric */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none print-gradient-overlay">
              <svg viewBox={layoutViewBox} className="w-full h-full" style={{maxWidth:`${layoutMaxWidth}px`, display:'block', margin:'0 auto'}} aria-hidden="true">
                <defs>
                  {inputs.depth_profile_type === 'sport_bottom' && (
                    <linearGradient id="pool-depth-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#93C5FD" />
                      <stop offset="100%" stopColor="#3B82F6" />
                    </linearGradient>
                  )}
                  {inputs.depth_profile_type === 'deep_end_hopper' && (
                    <linearGradient id="pool-depth-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#BAE6FD" />
                      <stop offset="100%" stopColor="#1E3A8A" />
                    </linearGradient>
                  )}
                  {inputs.depth_profile_type !== 'flat' && inputs.depth_profile_type !== 'sport_bottom' && inputs.depth_profile_type !== 'deep_end_hopper' && (
                    <linearGradient id="pool-depth-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#93C5FD" />
                      <stop offset="100%" stopColor="#3B82F6" />
                    </linearGradient>
                  )}
                </defs>
                {/* Depth tint over pool body */}
                <path d={poolPath} fill={inputs.depth_profile_type === 'flat' ? '#60A5FA' : 'url(#pool-depth-gradient)'} opacity="0.35" stroke="none" />
                {/* Compass rose — top-right corner of deck area */}
                <g transform={`translate(${layout.viewW - 16}, 16)`}>
                  <circle r="8" fill="white" stroke="#475569" strokeWidth="0.8" opacity="0.9" />
                  <line x1="0" y1="6" x2="0" y2="-6" stroke="#0F1D32" strokeWidth="1.5" />
                  <polygon points="0,-7 -2.5,-2 2.5,-2" fill="#0F1D32" />
                  <text x="0" y="-9" textAnchor="middle" fill="#0F1D32" fontSize="5" fontWeight="700" fontFamily="Manrope, sans-serif">N</text>
                </g>
                {/* Dimension labels — parametric positions */}
                {inputs.length && (
                  <>
                    <line x1={layout.ox + layout.poolW * 0.05} y1={layout.oy - 18} x2={layout.ox + layout.poolW * 0.95} y2={layout.oy - 18} stroke="#475569" strokeWidth="0.8" strokeDasharray="3 2" />
                    <line x1={layout.ox + layout.poolW * 0.05} y1={layout.oy - 21} x2={layout.ox + layout.poolW * 0.05} y2={layout.oy - 15} stroke="#475569" strokeWidth="0.8" />
                    <line x1={layout.ox + layout.poolW * 0.95} y1={layout.oy - 21} x2={layout.ox + layout.poolW * 0.95} y2={layout.oy - 15} stroke="#475569" strokeWidth="0.8" />
                    <rect x={layout.ox + layout.poolW * 0.5 - 25} y={layout.oy - 26} width="50" height="14" rx="2" fill="white" opacity="0.85" />
                    <text x={layout.ox + layout.poolW * 0.5} y={layout.oy - 16} textAnchor="middle" fill="#1E293B" fontSize="10" fontWeight="700" fontFamily="Manrope, sans-serif">{inputs.length}&apos;</text>
                  </>
                )}
                {inputs.width && (
                  <>
                    <line x1={layout.ox + layout.poolW + 18} y1={layout.oy + layout.poolH * 0.05} x2={layout.ox + layout.poolW + 18} y2={layout.oy + layout.poolH * 0.95} stroke="#475569" strokeWidth="0.8" strokeDasharray="3 2" />
                    <line x1={layout.ox + layout.poolW + 15} y1={layout.oy + layout.poolH * 0.05} x2={layout.ox + layout.poolW + 21} y2={layout.oy + layout.poolH * 0.05} stroke="#475569" strokeWidth="0.8" />
                    <line x1={layout.ox + layout.poolW + 15} y1={layout.oy + layout.poolH * 0.95} x2={layout.ox + layout.poolW + 21} y2={layout.oy + layout.poolH * 0.95} stroke="#475569" strokeWidth="0.8" />
                    <rect x={layout.ox + layout.poolW + 24} y={layout.oy + layout.poolH * 0.5 - 7} width="22" height="14" rx="2" fill="white" opacity="0.85" />
                    <text x={layout.ox + layout.poolW + 35} y={layout.oy + layout.poolH * 0.5 + 4} textAnchor="middle" fill="#1E293B" fontSize="10" fontWeight="700" fontFamily="Manrope, sans-serif">{inputs.width}&apos;</text>
                  </>
                )}
                {/* Scale bar — bottom-left of deck area */}
                {(() => {
                  const { ox, oy, poolH, scale } = layout;
                  const sbX = 8;
                  const sbY = oy + poolH + 30;
                  const seg = 8 * scale; // 8 feet
                  return (
                    <g>
                      <line x1={sbX} y1={sbY} x2={sbX + seg * 2} y2={sbY} stroke="#475569" strokeWidth="1" />
                      <line x1={sbX} y1={sbY - 3} x2={sbX} y2={sbY + 3} stroke="#475569" strokeWidth="1" />
                      <line x1={sbX + seg} y1={sbY - 3} x2={sbX + seg} y2={sbY + 3} stroke="#475569" strokeWidth="1" />
                      <line x1={sbX + seg * 2} y1={sbY - 3} x2={sbX + seg * 2} y2={sbY + 3} stroke="#475569" strokeWidth="1" />
                      <text x={sbX} y={sbY + 12} textAnchor="middle" fill="#64748B" fontSize="8" fontWeight="600" fontFamily="Manrope, sans-serif">0</text>
                      <text x={sbX + seg} y={sbY + 12} textAnchor="middle" fill="#64748B" fontSize="8" fontWeight="600" fontFamily="Manrope, sans-serif">8&apos;</text>
                      <text x={sbX + seg * 2} y={sbY + 12} textAnchor="middle" fill="#64748B" fontSize="8" fontWeight="600" fontFamily="Manrope, sans-serif">16&apos;</text>
                    </g>
                  );
                })()}
              </svg>
            </div>

            {/* Custom shape dashed overlay — SVG elements at parametric pool body coordinates */}
            {shape === 'geometric_custom' && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <svg viewBox={layoutViewBox} className="w-full h-full" style={{maxWidth:`${layoutMaxWidth}px`, display:'block', margin:'0 auto'}}>
                  <rect x={layout.ox} y={layout.oy} width={layout.poolW} height={layout.poolH} rx="3" fill="none" stroke="#64748B" strokeWidth="2.5" strokeDasharray="8 4" />
                  <text x={layout.ox + layout.poolW / 2} y={layout.oy + layout.poolH / 2 + 5} textAnchor="middle" fill="#64748B" fontSize="14" fontWeight="600" fontFamily="Manrope, sans-serif">Custom</text>
                </svg>
              </div>
            )}

            {/* Phase 5: Scaled feature indicators — HTML positioned in aspect-ratio container */}
            {!activeFeature && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="relative pointer-events-none" style={{width: '100%', maxWidth: `${layoutMaxWidth}px`, maxHeight: '100%', aspectRatio: layoutAspect}}>
                  {getFeatureSVGElements().map(({ id, geo, fill, stroke, opacity, strokeDash, strokeW, label, labelX, labelY }) => {
                    // Convert SVG coords to percentage of viewBox for CSS positioning
                    const vw = layout.viewW, vh = layout.viewH;
                    let left, top, width, height, borderRadius;
                    if (geo.type === 'circle') {
                      const d = geo.r * 2;
                      left = ((geo.cx - geo.r) / vw * 100) + '%';
                      top = ((geo.cy - geo.r) / vh * 100) + '%';
                      width = (d / vw * 100) + '%';
                      height = (d / vh * 100) + '%';
                      borderRadius = '50%';
                    } else if (geo.type === 'rect') {
                      left = (geo.x / vw * 100) + '%';
                      top = (geo.y / vh * 100) + '%';
                      width = (geo.w / vw * 100) + '%';
                      height = (geo.h / vh * 100) + '%';
                      borderRadius = (geo.rx || 2) + 'px';
                    } else {
                      // polygon/path — use label position as center, fixed size
                      left = ((labelX - 30) / vw * 100) + '%';
                      top = ((labelY - 30) / vh * 100) + '%';
                      width = (60 / vw * 100) + '%';
                      height = (60 / vh * 100) + '%';
                      borderRadius = '4px';
                    }
                    return (
                      <div key={id}
                        onClick={() => handleIndicatorClick(id)}
                        className="absolute flex items-center justify-center pointer-events-auto cursor-pointer"
                        style={{left, top, width, height, background: fill, border: `${strokeW}px ${strokeDash ? 'dashed' : 'solid'} ${stroke}`, borderRadius, opacity, zIndex: 5}}
                        data-testid={`feature-indicator-${id}`}>
                        <span className="text-white font-extrabold text-center leading-tight" style={{fontSize: '9px', textShadow: '0 1px 3px rgba(0,0,0,0.6)', pointerEvents: 'none'}}>{label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Free placement click/drop target — full canvas, above all non-interactive layers, below zone overlays */}
            {(placingFeature || draggingFeature) && (
              <div className="absolute inset-0 z-10"
                style={{cursor: 'crosshair'}}
                onClick={placingFeature ? handleCanvasClick : draggingFeature ? handleCanvasClickDrop : undefined}
                onDragOver={draggingFeature ? handleCanvasDragOver : undefined}
                onDrop={draggingFeature ? handleCanvasDrop : undefined}
                data-testid="canvas-free-target" />
            )}

            {/* Zone overlays — positioned inside a container matching the SVG's rendered area */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
              <div className="relative pointer-events-none" style={{width: '100%', maxWidth: `${layoutMaxWidth}px`, maxHeight: '100%', aspectRatio: layoutAspect}}>
                {validZones.map(zone => {
                  const style = getZoneStyle(zone);
                  const isHovered = hoveredZone === zone.id;
                  return (
                    <div key={zone.id}
                      onClick={() => handleZoneClick(zone)}
                      onDragOver={handleZoneDragOver}
                      onDragEnter={() => handleZoneDragEnter(zone.id)}
                      onDragLeave={(e) => handleZoneDragLeave(e, zone.id)}
                      onDrop={(e) => handleZoneDrop(e, zone)}
                      className="absolute rounded-lg cursor-pointer pointer-events-auto transition-all hover:scale-[1.02]"
                      style={{...style, background: isHovered ? 'rgba(245,158,11,0.5)' : 'rgba(245,158,11,0.25)', border: isHovered ? '3px solid #B45309' : '2.5px solid #D97706', animation: 'pulse 2s ease-in-out infinite', transform: isHovered ? 'scale(1.03)' : undefined}}
                      data-testid={`zone-overlay-${zone.id}`}>
                      <div className="absolute inset-0 flex items-center justify-center" style={{pointerEvents:'none'}}>
                        <span className="text-[11px] font-bold text-[#92400E] bg-white/80 px-2 py-0.5 rounded shadow-sm">{zone.label}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          {/* Export dropdown + hidden print elements */}
          <ExportDropdown inputs={inputs} featureStatus={featureStatus} spaType={spaType} layout={layout} />
        </div>
      </div>
    </div>
  );
}

function ExportDropdown({ inputs, featureStatus, spaType, layout }) {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const placedFeatures = featureStatus.filter(f => f.placed);
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const projectAddr = [inputs.address, inputs.city].filter(Boolean).join(', ') || 'Address TBD';
  const customerName = inputs.customer_name || 'Customer TBD';
  const drawnBy = user?.name || 'Staff';

  const triggerPrint = (mode) => {
    setOpen(false);
    const label = mode === 'customer' ? 'Customer' : 'Permit';
    toast.info(`Preparing ${label} plan for print...`);
    document.body.classList.add(`print-${mode}`);
    setTimeout(() => {
      window.print();
    }, 300);
    const cleanup = () => {
      document.body.classList.remove(`print-${mode}`);
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
  };

  return (
    <>
      <div className="px-4 py-2 border-t border-slate-100 flex justify-end print-hide" ref={dropdownRef}>
        <div className="relative">
          <button onClick={() => setOpen(!open)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:border-[#F59E0B] hover:text-[#92400E] transition-colors"
            data-testid="export-plan-btn">
            <Printer className="w-3 h-3" />
            Export Plan
            <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor"><path d="M1 3l3 3 3-3"/></svg>
          </button>
          {open && (
            <div className="absolute bottom-full right-0 mb-1 w-44 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden z-50">
              <button onClick={() => triggerPrint('customer')}
                className="w-full px-3 py-2 text-left text-xs font-medium text-slate-700 hover:bg-amber-50 hover:text-[#92400E] flex items-center gap-2"
                data-testid="export-customer-plan">
                <span className="w-2 h-2 rounded-full bg-[#F59E0B]" />
                Customer Plan
              </button>
              <button onClick={() => triggerPrint('permit')}
                className="w-full px-3 py-2 text-left text-xs font-medium text-slate-700 hover:bg-slate-50 flex items-center gap-2 border-t border-slate-100"
                data-testid="export-permit-plan">
                <span className="w-2 h-2 rounded-full bg-slate-500" />
                Permit Plan
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Hidden print elements — Customer Plan */}
      <div className="print-customer-header" style={{display:'none'}}>
        <div style={{padding:'16px 32px', borderBottom:'2px solid #0F1D32', display:'flex', justifyContent:'space-between', alignItems:'center', background:'white'}}>
          <div>
            <h1 style={{fontSize:'18px', fontWeight:800, color:'#0F1D32', fontFamily:'Cormorant Garamond, serif', margin:0}}>HDH Pools & Spas</h1>
            <p style={{fontSize:'11px', color:'#64748B', margin:'2px 0 0'}}>{projectAddr}</p>
          </div>
          <div style={{textAlign:'right'}}>
            <p style={{fontSize:'11px', color:'#64748B', margin:0}}>{customerName}</p>
            <p style={{fontSize:'10px', color:'#94A3B8', margin:'2px 0 0'}}>{today}</p>
          </div>
        </div>
      </div>
      <div className="print-customer-footer" style={{display:'none'}}>
        <div style={{padding:'12px 32px', borderTop:'1px solid #E2E8F0', display:'flex', justifyContent:'space-between', background:'white'}}>
          <p style={{fontSize:'9px', color:'#94A3B8', fontStyle:'italic', margin:0}}>Preliminary Design — Subject to Change</p>
          <p style={{fontSize:'9px', color:'#94A3B8', margin:0}}>HDH Pools & Spas | hdhpools.com</p>
        </div>
        {placedFeatures.length > 0 && (
          <div style={{padding:'0 32px 12px', background:'white'}}>
            <p style={{fontSize:'9px', fontWeight:700, color:'#475569', margin:'0 0 4px'}}>Features:</p>
            <p style={{fontSize:'9px', color:'#64748B', margin:0}}>{placedFeatures.map(f => {
              if (f.id === 'spa') return `Spa (${spaType})`;
              return f.label;
            }).join(' | ')}</p>
          </div>
        )}
      </div>

      {/* Hidden print elements — Permit Plan */}
      <div className="print-permit-titleblock" style={{display:'none'}}>
        <div style={{padding:'10px 32px', borderTop:'2px solid #000', display:'flex', justifyContent:'space-between', alignItems:'center', background:'white', fontFamily:'Manrope, sans-serif'}}>
          <div>
            <p style={{fontSize:'10px', fontWeight:700, color:'#000', margin:0}}>HDH Pools & Spas | LIC #[YOUR LICENSE] | hdhpools.com</p>
          </div>
          <div style={{textAlign:'center'}}>
            <p style={{fontSize:'10px', fontWeight:600, color:'#000', margin:0}}>{projectAddr}</p>
            <p style={{fontSize:'9px', color:'#333', margin:'1px 0 0'}}>{customerName}</p>
          </div>
          <div style={{textAlign:'right'}}>
            <p style={{fontSize:'9px', color:'#333', margin:0}}>{today} | Scale: 1/8&quot;=1&apos; | Sheet: 1 of 1</p>
            <p style={{fontSize:'9px', color:'#333', margin:'1px 0 0'}}>Drawn by: {drawnBy}</p>
          </div>
        </div>
      </div>
      <div className="print-permit-disclaimer" style={{display:'none'}}>
        <div style={{padding:'6px 32px', background:'white', textAlign:'center'}}>
          <p style={{fontSize:'10px', fontWeight:800, color:'#DC2626', margin:0, letterSpacing:'0.5px'}}>NOT FOR CONSTRUCTION WITHOUT ENGINEER STAMP</p>
        </div>
      </div>

      {/* Permit plan feature dimension callouts */}
      <div className="print-permit-callouts" style={{display:'none'}}>
        <div style={{padding:'4px 32px', background:'white'}}>
          <p style={{fontSize:'9px', color:'#000', margin:'0 0 2px', fontWeight:600}}>Feature Dimensions:</p>
          <p style={{fontSize:'8px', color:'#333', margin:0}}>
            {placedFeatures.map(f => {
              if (f.id === 'steps') return 'Steps: 4\u2032\u00d74\u2032';
              if (f.id === 'tanning-ledge') return 'Tanning Ledge: 8\u2032\u00d71.5\u2032';
              if (f.id === 'bench') return 'Bench: wall length \u00d7 1.5\u2032';
              if (f.id === 'spa') return `Spa (${spaType}): 8\u2032 dia`;
              if (f.id === 'water-feature') return 'Water Feature: 2\u2032\u00d72\u2032';
              if (f.id === 'fire-feature') return 'Fire Feature: 2\u2032\u00d72\u2032';
              if (f.id === 'slide') return 'Slide: 3\u2032\u00d76\u2032';
              return f.label;
            }).join(' | ')}
          </p>
        </div>
      </div>
    </>
  );
}

function DepthProfileTooltip() {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);

  const show = () => { clearTimeout(closeTimer.current); setOpen(true); };
  const hide = () => { closeTimer.current = setTimeout(() => setOpen(false), 150); };

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  return (
    <span className="relative inline-block" onMouseEnter={show} onMouseLeave={hide}>
      <button type="button" aria-label="Depth profile info" onFocus={show} onBlur={hide}
        onKeyDown={e => { if (e.key === 'Escape') setOpen(false); }}
        className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-slate-300 text-slate-400 hover:text-[#F59E0B] hover:border-[#F59E0B] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F59E0B] focus-visible:ring-offset-1"
        data-testid="depth-profile-info-btn">
        <Info className="w-2.5 h-2.5" />
      </button>
      {open && (
        <div onMouseEnter={show} onMouseLeave={hide}
          className="absolute left-0 top-6 z-50 w-64 p-3 bg-white border border-slate-200 rounded-lg shadow-lg"
          role="tooltip" data-testid="depth-profile-tooltip">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Depth Profile Options</p>
          {Object.entries(DEPTH_PROFILES).map(([k, v]) => (
            <div key={k} className="mb-1.5 last:mb-0">
              <p className="text-xs font-semibold text-[#0F1D32]">{v.label}</p>
              <p className="text-[11px] text-slate-500 leading-snug">{v.tooltip}</p>
            </div>
          ))}
        </div>
      )}
    </span>
  );
}

